/*    
<summary>
   This typescript ts file contain all the formik form validations.
   Developer:Aashish Singh, Created Date:29-March-2023
</summary>
*/
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import moment from "moment";
import * as Yup from "yup";
import { integerNumJpRegex, integerRegex, IsStoreClosedByDateForShift, isTimeOuterThanShift, stringJpRegexValidate } from "../../constants/constants";
import addInventoryTypeEnum from "../../constants/enums/add-inventory-type-enum";
import emailTypeEnum from "../../constants/enums/email-type-enum";
import userTypeEnum from "../../constants/enums/user-type-enum";
import config from "../../helpers/config-helper";
import { toHalfWidth } from "../../helpers/validate-helper";
import { formatMessage } from "../../translations/format-message";
dayjs.extend(isSameOrAfter);

const appConfig = config();

const macAddressRegex = /^([0-9A-Fa-f]{2}([-:])){5}[0-9A-Fa-f]{2}$/;
const ipv4Regex =
  /^(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])$/;
const ipv6Regex =
  /^(([0-9a-fA-F]{1,4}:){7}([0-9a-fA-F]{1,4}|:)|(([0-9a-fA-F]{1,4}:){1,6}|:):([0-9a-fA-F]{1,4}|:){1,5}([0-9a-fA-F]{1,4}|:)|::)$/;

const ipRegex = new RegExp(`(${ipv4Regex.source})|(${ipv6Regex.source})`);
const iframeRegex = /^<iframe.*?src=["'].*?["'].*?>.*?<\/iframe>$/i;

const fullWidthToHalfWidth = (val: string) =>
  val.replace(/[\uff10-\uff19]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xfee0));

export const ChangeLoggedInUserPasswordValidateSchema = Yup.object({
  OldPassword: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    // .matches(/^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/, "user_validation_password_matches")
    .required("field_required"),
  NewPassword: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .required("field_required")
    .test("passwords-not-same", "old_new_pass_same", function (value, context) {
      if (value == context.parent?.OldPassword) {
        return false;
      }
      return true;
    }),
});

export const ChangeAgreementPasswordValidateSchema = Yup.object().shape({
  Password: Yup.string()
    .required("field_required")
    .min(4, "min_4_length_required")
    .max(4, "max_4_length_required")
    .matches(integerRegex, "only_numbers_allowed"),
  ConfirmPassword: Yup.string().when("Password", {
    is: (password: string) => password && password.length === 4,
    then: (schema) =>
      schema
        .required("field_required")
        .oneOf([Yup.ref("Password")], "password_did_not_match"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const UserChangePasswordValidateSchema = Yup.object({
  NewPassword: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .required("field_required"),
});

export const SendPasswordResetLinkValidationSchema = Yup.object({
  Email: Yup.string()
    .matches(
      /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([一-龠ぁ-ゔァ-ヴー々〆〤a-zA-Z\-0-9]+\.)+[一-龠ぁ-ゔァ-ヴー々〆〤a-zA-Z]{2,}))$/,
      "invalid_login_id"
    )
    .min(5, "lodin_id_length_required")
    .max(50, "lodin_id_length_required")
    .required("field_required"),
});

export const StoreChangePasswordValidateSchema = Yup.object({
  NewPassword: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .required("field_required"),
  ConfirmPassword: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .oneOf([Yup.ref("NewPassword")], "validation_new_confirm_password_mismatch")
    .required("field_required"),
});

export const CustomerEmailValidation = Yup.object({
  Email: Yup.string()
    .required("email_is_required")
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address"),
});

export const CustomerPhone1Validation = Yup.object({
  Phone1: Yup.string()
    .matches(integerRegex, "only_numbers_are_allowed")
    .test("start-with-zero", "phone_number_must_start_with_0", (val) => {
      if (!val) return false;
      const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
      return digits.startsWith("0");
    })
    .test(
      "valid-japanese-phone",
      "telephone_10_or_11_length",
      (val) => {
        if (!val) return false;
        const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
        return digitsOnly.length === 10 || digitsOnly.length === 11;
      }
    )
  .required("field_required"),
});


export const CustomerPhone2Validation = Yup.object({
  Phone2: Yup.string()
    .matches(integerRegex, "only_numbers_are_allowed")
    .test("start-with-zero", "phone_number_must_start_with_0", (val) => {
      if (!val) return false;
      const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
      return digits.startsWith("0");
    })
    .test(
      "valid-japanese-phone",
      "telephone_10_or_11_length",
      (val) => {
        if (!val) return false;
        const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
        return digitsOnly.length === 10 || digitsOnly.length === 11;
      }
    ),
});

export const ConsumptionTaxValidateSchema = Yup.object().shape({
  TaxValue: Yup.number()
    .typeError("tax_value_must_be_a_number")
    .min(1, "tax_value_must_be_at_least_1")
    .max(100, "tax_value_must_not_exceed_100")
    .required("field_required"),
});

export const LoginValidateSchema = Yup.object({
  email: Yup.string().trim().required("field_required"),
  password: Yup.string().trim().required("field_required"),
});
export const CustomerLoginValidateSchema = Yup.object({
  email: Yup.string().trim().required("field_required"),
  password: Yup.string().trim().required("field_required"),
});
export const LoginValidateSchemaForStore = Yup.object({
  storeCode: Yup.string().trim().required("field_required"),
  password: Yup.string().trim().required("field_required"),
});
export const AddUserValidateSchema = Yup.object({
  UserName: Yup.string()
    .min(5, "min_user_name_length_required")
    .max(25, "min_user_name_length_required")
    .required("field_required")
    .test("no-leading-whitespace", "whitespace_error", (value) => {
      if (typeof value !== "string") return false;
      return !value.startsWith(" ");
    }),
  Password: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .required("field_required"),
  Email: Yup.string()
    .matches(
      /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([一-龠ぁ-ゔァ-ヴー々〆〤a-zA-Z\-0-9]+\.)+[一-龠ぁ-ゔァ-ヴー々〆〤a-zA-Z]{2,}))$/,
      "invalid_login_id"
    )
    .min(5, "lodin_id_length_required")
    .max(50, "lodin_id_length_required")
    .required("field_required"),
  UserType: Yup.number().min(0, "field_required"),
});

export const EditUserValidateSchema = Yup.object({
  UserName: Yup.string()
    .required("field_required")
    .min(5, "min_user_name_length_required")
    .max(25, "min_user_name_length_required"),
  Email: Yup.string()
    .matches(
      /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([一-龠ぁ-ゔァ-ヴー々〆〤a-zA-Z\-0-9]+\.)+[一-龠ぁ-ゔァ-ヴー々〆〤a-zA-Z]{2,}))$/,
      "invalid_login_id"
    )
    .min(5, "login_id_length_required")
    .max(50, "login_id_length_required")
    .required("field_required"),
  UserType: Yup.number().min(0, "field_required"),
});

export const EmptyValidateSchema = Yup.object({});

export const AddParkingLotDetailsValidateSchema = Yup.object({
  BranchId: Yup.number().min(1, "field_required"),
  CurrentStatus: Yup.number().min(0, "field_required"),
  PlName: Yup.string()
    .max(20, "char_limit_reached")
    .required("field_required")
    .test("no-leading-whitespace", "whitespace_error", (value) => {
      if (typeof value !== "string") return false;
      return !value.startsWith(" ");
    }),
  ParkingLotId: Yup.string()
    .max(20, "char_limit_reached")
    .required("field_required")
    .test("no-leading-whitespace", "whitespace_error", (value) => {
      if (typeof value !== "string") return false;
      return !value.startsWith(" ");
    }),
  Comment: Yup.string().max(500, "comment_limit_reach"),
});

export const AddParkingLotCategoryDetailsValidateSchema = Yup.object().shape({
  ParkingLotsCategories: Yup.array().of(
    Yup.object().shape({
      Description: Yup.string()
        .max(20, "char_limit_reached")
        .required("field_required")
        .test("no-leading-whitespace", "whitespace_error", (value) => {
          if (typeof value !== "string") return false;
          return !value.startsWith(" ");
        }),
    })
  ),
});

export const AddMediaDefaultFolderValidateSchema = Yup.object({
  FolderName: Yup.string()
    .max(20, "char_limit_reached")
    .required("field_required")
    .test("no-leading-whitespace", "whitespace_error", (value) => {
      if (typeof value !== "string") return false;
      return !value.startsWith(" ");
    }),
  FolderCategoryType: Yup.number()
    .min(0, "field_required")
    .required("field_required"),
  Comment: Yup.string().max(500, "comment_limit_reach"),
});

export const selectCSVValidateSchema = Yup.object({
  CSVFile: Yup.mixed()
    .nullable()
    .required("field_required")
    .test("csv", "only_csv", (value: any) => {
      let val: any;
      if (value) {
        val = value.split(".");
        return val[1].toLowerCase() === "csv";
      } else return false;
    }),
});

export const AddMediaFolderValidateSchema = Yup.object({
  FolderName: Yup.string()
    .max(20, "char_limit_reached")
    .required("field_required")
    .test("no-leading-whitespace", "whitespace_error", (value) => {
      if (typeof value !== "string") return false;
      return !value.startsWith(" ");
    }),
  LeftMargin: Yup.string()
    .required("field_required")
    .matches(/^-?[0-9]+$/, "margin_field")

    .test("LeftMargin", "margin_limit_reached", (value: any) => {
      if (value?.length > 10) {
        return false;
      }
      return true;
    }),
  TopMargin: Yup.string()
    .required("field_required")
    .matches(/^-?[0-9]+$/, "margin_field")
    .test("TopMargin", "margin_limit_reached", (value: any) => {
      if (value?.length > 10) {
        return false;
      }
      return true;
    }),
  FolderCategoryType: Yup.number()
    .min(0, "field_required")
    .required("field_required"),
  Comment: Yup.string().max(500, "comment_limit_reach"),
});

export const AddParkingLotLinkBaseValidateSchema = Yup.object({
  ParkingLotInputTypeEnum: Yup.number().min(0, "field_required"),
  ParkingLotOutputTypeEnum: Yup.number()
    .min(0, "field_required")
    .test(
      "ParkingLotInputTypeEnum",
      "linkbase_in_out_validation",
      function (value) {
        if (
          this.parent.ParkingLotInputTypeEnum == 2 &&
          this.parent.ParkingLotOutputTypeEnum == 1
        ) {
          return false;
        }
        return true;
      }
    ),
});

export const AddMediaPriceValidateSchema = Yup.object({
  MediaName: Yup.string()
    .required("field_required")
    .max(20, "char_limit_reached"),
  PricingValue: Yup.number()
    .typeError("only_digits")
    .min(0, "pricing_range")
    .max(9, "pricing_range")
    .required("field_required"),
});

export const AddMediaHeightWidthalidateSchema = Yup.object({
  MediaName: Yup.string()
    .required("field_required")
    .max(20, "char_limit_reached"),
  Height: Yup.number()
    .min(0, "height_max_min")
    .max(20000, "height_max_min")
    .required("field_required"),
  Width: Yup.number()
    .min(0, "width_max_min")
    .max(20000, "width_max_min")
    .required("field_required"),
});

export const AddMediaValidateSchema = Yup.object({
  MediaName: Yup.string()
    .required("field_required")
    .max(20, "char_limit_reached"),
});

export const AddLinkbaseOutValidationSchema = Yup.object({
  SoracomId: Yup.number().min(1, "field_required").required("field_required"),
  RelayType: Yup.number().min(0, "field_required").required("field_required"),
  SimId: Yup.string()
    .required("field_required")
    .max(30, "iccid_length_required")
    .test("must_greater-than-0", "sim_number_min", function (SimId) {
      if (SimId) return SimId ? (BigInt(SimId) > 0 ? true : false) : false;
      else return true;
    }),
});

export const AddLinkbaseOutDetailsValidationSchema = Yup.object({
  LinkBaseOutRelays: Yup.array().of(
    Yup.object().shape({
      TargetType: Yup.number().test(
        "TargetType",
        "field_required_target_type",
        (value, context) => {
          let involvedPaths = [
            "LinkBaseOutRelays[0].TargetType",
            "LinkBaseOutRelays[4].TargetType",
            "LinkBaseOutRelays[8].TargetType",
            "LinkBaseOutRelays[10].TargetType",
          ];
          if (involvedPaths.includes(context.path)) {
            if (Number(value) === -1 && context.parent?.Disabled === false) {
              return false;
            }
          }
          return true;
        }
      ),
      ParkingLotTarget: Yup.string().test(
        "ParkingLotTarget",
        "field_required_parking_lot",
        (value, context) => {
          let involvedPaths = [
            "LinkBaseOutRelays[0].ParkingLotTarget",
            "LinkBaseOutRelays[4].ParkingLotTarget",
            "LinkBaseOutRelays[8].ParkingLotTarget",
            "LinkBaseOutRelays[10].ParkingLotTarget",
          ];
          if (involvedPaths.includes(context.path)) {
            if (
              Number(context.parent?.TargetType) === 0 &&
              Number(value) === -1
            ) {
              return false;
            }
          }
          return true;
        }
      ),
      SlotTarget: Yup.string().test(
        "SlotTarget",
        "field_required_slot",
        (value, context) => {
          let involvedPaths = [
            "LinkBaseOutRelays[0].SlotTarget",
            "LinkBaseOutRelays[4].SlotTarget",
            "LinkBaseOutRelays[8].SlotTarget",
            "LinkBaseOutRelays[10].SlotTarget",
          ];
          if (involvedPaths.includes(context.path)) {
            if (
              Number(context.parent?.TargetType) === 1 &&
              Number(value) === -1
            ) {
              return false;
            }
          }
          return true;
        }
      ),
      SlotTypeTarget: Yup.string().test(
        "SlotTypeTarget",
        "field_required_slot_type",
        (value, context) => {
          let involvedPaths = [
            "LinkBaseOutRelays[0].SlotTypeTarget",
            "LinkBaseOutRelays[4].SlotTypeTarget",
            "LinkBaseOutRelays[8].SlotTypeTarget",
            "LinkBaseOutRelays[10].SlotTypeTarget",
          ];
          if (involvedPaths.includes(context.path)) {
            if (
              Number(context.parent?.TargetType) === 2 &&
              Number(value) === -1
            ) {
              return false;
            }
          }
          return true;
        }
      ),
      Comment: Yup.string().test(
        "Comment",
        "char_limit_reached_comment",
        (value, context) => {
          if (
            context.parent?.Comment?.length > 56 &&
            context.parent?.Disabled == false
          ) {
            return false;
          }
          return true;
        }
      ),
    })
  ),
});

export const AddIntegratorLinkbaseOutDetailsValidationSchema = Yup.object({
  LinkBaseOutRelays: Yup.array().of(
    Yup.object().shape({
      Target: Yup.number()
        .nullable()
        .test("Target", "field_required", function (value, contexts) {
          let involvedPaths = [
            "LinkBaseOutRelays[0].Target",
            "LinkBaseOutRelays[4].Target",
            "LinkBaseOutRelays[8].Target",
            "LinkBaseOutRelays[10].Target",
          ];
          const { Disabled } = this.parent;
          if (involvedPaths.includes(contexts.path)) {
            if (Disabled == true && this.originalValue === undefined) {
              return true;
            }

            if (
              Disabled == false &&
              (this.originalValue === undefined ||
                this.originalValue === "-1" ||
                this.originalValue === -1)
            ) {
              return false;
            }
          }
          return true;
        }),
      Comment: Yup.string().test(
        "Comment",
        "char_limit_reached_comment",
        (value, context) => {
          if (
            context.parent?.Comment?.length > 56 &&
            context.parent?.Disabled == false
          ) {
            return false;
          }
          return true;
        }
      ),
    })
  ),
});

export const AddMediaFileValidateSchema = Yup.object({
  File: Yup.mixed()
    .nullable()
    .required("field_required")
    .test("csv", "only_media_files", (value: any) => {
      let val;
      if (value) {
        val = value?.split(".");
        return (
          val[1].toLowerCase() === "jpg" ||
          val[1].toLowerCase() === "jpeg" ||
          val[1].toLowerCase() === "png" ||
          val[1].toLowerCase() === "svg" ||
          val[1].toLowerCase() === "gif" ||
          val[1].toLowerCase() === "mp4"
        );
      } else return false;
    }),
});

export const AddPricingMediaImageFileValidateSchema = Yup.object({
  File0: Yup.mixed()
    .nullable()
    .required("field_required")
    .test("csv", "only_media_files", (value: any) => {
      let val;
      if (value) {
        val = value?.split(".");
        return (
          val[1].toLowerCase() === "jpg" ||
          val[1].toLowerCase() === "jpeg" ||
          val[1].toLowerCase() === "png" ||
          val[1].toLowerCase() === "svg" ||
          val[1].toLowerCase() === "gif" ||
          val[1].toLowerCase() === "mp4"
        );
      } else return false;
    }),
  File1: Yup.mixed()
    .nullable()
    .required("field_required")
    .test("csv", "only_media_files", (value: any) => {
      let val;
      if (value) {
        val = value?.split(".");
        return (
          val[1].toLowerCase() === "jpg" ||
          val[1].toLowerCase() === "jpeg" ||
          val[1].toLowerCase() === "png" ||
          val[1].toLowerCase() === "svg" ||
          val[1].toLowerCase() === "gif" ||
          val[1].toLowerCase() === "mp4"
        );
      } else return false;
    }),
  File2: Yup.mixed()
    .nullable()
    .required("field_required")
    .test("csv", "only_media_files", (value: any) => {
      let val;
      if (value) {
        val = value?.split(".");
        return (
          val[1].toLowerCase() === "jpg" ||
          val[1].toLowerCase() === "jpeg" ||
          val[1].toLowerCase() === "png" ||
          val[1].toLowerCase() === "svg" ||
          val[1].toLowerCase() === "gif" ||
          val[1].toLowerCase() === "mp4"
        );
      } else return false;
    }),
  File3: Yup.mixed()
    .nullable()
    .required("field_required")
    .test("csv", "only_media_files", (value: any) => {
      let val;
      if (value) {
        val = value?.split(".");
        return (
          val[1].toLowerCase() === "jpg" ||
          val[1].toLowerCase() === "jpeg" ||
          val[1].toLowerCase() === "png" ||
          val[1].toLowerCase() === "svg" ||
          val[1].toLowerCase() === "gif" ||
          val[1].toLowerCase() === "mp4"
        );
      } else return false;
    }),
  File4: Yup.mixed()
    .nullable()
    .required("field_required")
    .test("csv", "only_media_files", (value: any) => {
      let val;
      if (value) {
        val = value?.split(".");
        return (
          val[1].toLowerCase() === "jpg" ||
          val[1].toLowerCase() === "jpeg" ||
          val[1].toLowerCase() === "png" ||
          val[1].toLowerCase() === "svg" ||
          val[1].toLowerCase() === "gif" ||
          val[1].toLowerCase() === "mp4"
        );
      } else return false;
    }),
  File5: Yup.mixed()
    .nullable()
    .required("field_required")
    .test("csv", "only_media_files", (value: any) => {
      let val;
      if (value) {
        val = value?.split(".");
        return (
          val[1].toLowerCase() === "jpg" ||
          val[1].toLowerCase() === "jpeg" ||
          val[1].toLowerCase() === "png" ||
          val[1].toLowerCase() === "svg" ||
          val[1].toLowerCase() === "gif" ||
          val[1].toLowerCase() === "mp4"
        );
      } else return false;
    }),
  File6: Yup.mixed()
    .nullable()
    .required("field_required")
    .test("csv", "only_media_files", (value: any) => {
      let val;
      if (value) {
        val = value?.split(".");
        return (
          val[1].toLowerCase() === "jpg" ||
          val[1].toLowerCase() === "jpeg" ||
          val[1].toLowerCase() === "png" ||
          val[1].toLowerCase() === "svg" ||
          val[1].toLowerCase() === "gif" ||
          val[1].toLowerCase() === "mp4"
        );
      } else return false;
    }),
  File7: Yup.mixed()
    .nullable()
    .required("field_required")
    .test("csv", "only_media_files", (value: any) => {
      let val;
      if (value) {
        val = value?.split(".");
        return (
          val[1].toLowerCase() === "jpg" ||
          val[1].toLowerCase() === "jpeg" ||
          val[1].toLowerCase() === "png" ||
          val[1].toLowerCase() === "svg" ||
          val[1].toLowerCase() === "gif" ||
          val[1].toLowerCase() === "mp4"
        );
      } else return false;
    }),
  File8: Yup.mixed()
    .nullable()
    .required("field_required")
    .test("csv", "only_media_files", (value: any) => {
      let val;
      if (value) {
        val = value?.split(".");
        return (
          val[1].toLowerCase() === "jpg" ||
          val[1].toLowerCase() === "jpeg" ||
          val[1].toLowerCase() === "png" ||
          val[1].toLowerCase() === "svg" ||
          val[1].toLowerCase() === "gif" ||
          val[1].toLowerCase() === "mp4"
        );
      } else return false;
    }),
  File9: Yup.mixed()
    .nullable()
    .required("field_required")
    .test("csv", "only_media_files", (value: any) => {
      let val;
      if (value) {
        val = value?.split(".");
        return (
          val[1].toLowerCase() === "jpg" ||
          val[1].toLowerCase() === "jpeg" ||
          val[1].toLowerCase() === "png" ||
          val[1].toLowerCase() === "gif" ||
          val[1].toLowerCase() === "svg" ||
          val[1].toLowerCase() === "mp4"
        );
      } else return false;
    }),
});

export const ForgetPasswordValidateSchema = Yup.object({
  Email: Yup.string()
    .matches(
      /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([一-龠ぁ-ゔァ-ヴー々〆〤a-zA-Z\-0-9]+\.)+[一-龠ぁ-ゔァ-ヴー々〆〤a-zA-Z]{2,}))$/,
      "invalid_login_id"
    )
    .min(5, "lodin_id_length_required")
    .max(50, "lodin_id_length_required")
    .required("field_required"),
});

export const ResetPasswordValidateSchema = Yup.object({
  NewPassword: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .required("field_required"),
  ConfirmPassword: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .oneOf([Yup.ref("NewPassword")], "validation_new_confirm_password_mismatch")
    .required("field_required"),
});


export const EditStoreValidationSchema = Yup.object().shape({
  StoreName: Yup.string()
    .required("field_required")
    .max(25, "store_name_max_25_characters"),
  KanaName: Yup.string()
    .required("field_required")
    .matches(stringJpRegexValidate, "only_kana_are_allowed")
    .max(25, "store_name_max_25_characters"),
  StoreOwnerName: Yup.string()
    .required("field_required")
    .max(25, "owner_name_max_25_characters"),
  BusinessTypeId: Yup.number()
    .required("field_required")
    .min(1, "field_required"),
  StoreCategory: Yup.string().oneOf(["0", "1"]).required("field_required"),
  Email: Yup.string()
    .required("email_is_required")
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address"),
  PostalCode: Yup.string()
    .matches(integerNumJpRegex, "only_numbers_are_allowed")
    .required("field_required")
    .length(7, "postal_code_max_7_characters"),
  Address: Yup.string()
    .required("field_required")
    .max(256, "max_256_characters")
    .test(
      "field_required_address",
      "field_required_address",
      function (value, context) {
        const orignal = value.replace(context?.parent?.PrefectureName, "");
        if (orignal.trim() == "") {
          return false;
        }
        return true;
      }
    ),
  MobileNumber: Yup.string()
    .matches(integerRegex, "only_numbers_are_allowed")
    .test("start-with-zero", "phone_number_must_start_with_0", (val) => {
      if (!val) return false;
      const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
      return digits.startsWith("0");
    })
    .test(
      "valid-japanese-phone",
      "telephone_10_or_11_length",
      (val) => {
        if (!val) return false;
        const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
        return digitsOnly.length === 10 || digitsOnly.length === 11;
      }
    )
    .required("field_required"),
  Status: Yup.string().oneOf(["0", "1"]).required("field_required"),
  IsNominationFee: Yup.string().oneOf(["0", "1"]).required("field_required"),
  StoreLink: Yup.string()
    .max(1024, "url_char_limit_reached")
    .matches(
      /^(https:\/\/(www\.)?|http:\/\/(www\.)?)[A-Za-z0-9-_.:/]+$/,
      "invalid_url"
    ),
  Url: Yup.string()
    .max(1024, "url_char_limit_reached")
    .url(
      "invalid_url"
    ),
  StoreCode: Yup.string()
    .required("field_required")
    .max(10, "10_char_limit_reached")
    .matches(
      /^[A-Za-z0-9]+$/,
      "only_alphanumeric_allowed"
    ),
});
export const AddStoreValidationSchema = Yup.object().shape({
  StoreName: Yup.string()
    .required("field_required")
    .max(25, "store_name_max_25_characters"),
  KanaName: Yup.string()
    .required("field_required")
    .matches(stringJpRegexValidate, "only_kana_are_allowed")
    .max(25, "store_name_max_25_characters"),
  StoreOwnerName: Yup.string()
    .required("field_required")
    .max(25, "owner_name_max_25_characters"),
  BusinessTypeId: Yup.number()
    .required("field_required")
    .min(1, "field_required"),
  StoreCategory: Yup.string().oneOf(["0", "1"]).required("field_required"),
  Email: Yup.string()
    .required("email_is_required")
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address"),
  PostalCode: Yup.string()
    .matches(integerNumJpRegex, "only_numbers_are_allowed")
    .required("field_required")
    .length(7, "postal_code_max_7_characters"),
  Address: Yup.string()
    .required("field_required")
    .max(256, "max_256_characters")
    .test(
      "field_required_address",
      "field_required_address",
      function (value, context) {
        const orignal = value.replace(context?.parent?.PrefectureName, "");
        if (orignal.trim() == "") {
          return false;
        }
        return true;
      }
    ),
  MobileNumber: Yup.string()
    .matches(integerRegex, "only_numbers_are_allowed")
    .test("start-with-zero", "phone_number_must_start_with_0", (val) => {
      if (!val) return false;
      const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
      return digits.startsWith("0");
    })
    .test(
      "valid-japanese-phone",
      "telephone_10_or_11_length",
      (val) => {
        if (!val) return false;
        const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
        return digitsOnly.length === 10 || digitsOnly.length === 11;
      }
    )
    .required("field_required"),
  Status: Yup.string().oneOf(["0", "1"]).required("field_required"),
  IsNominationFee: Yup.string().oneOf(["0", "1"]).required("field_required"),
StoreLink: Yup.string()
  .max(1024, "url_char_limit_reached")
  .test("is-valid-url", "Invalid URL", function (value) {
    const baseUrl = process.env.REACT_APP_BASE_URL_STORE || "";
    const finalUrl = `${baseUrl}${value || ""}`.trim();

    try {
      new URL(finalUrl); // if invalid, throws error
      return true;
    } catch {
      return false;
    }
  }),
  Url: Yup.string()
    .max(1024, "url_char_limit_reached")
    .url(
      "invalid_url"
    ),
  Password: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .required("field_required"),
  ConfirmPassword: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .oneOf([Yup.ref("Password")], "validation_new_confirm_password_mismatch")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .required("field_required"),
  StoreCode: Yup.string()
    .required("field_required")
    .max(10, "10_char_limit_reached")
    .matches(
      /^[A-Za-z0-9]+$/,
      "only_alphanumeric_allowed"
    ),
});

export const UpdateStoreDetailsValidationSchema = Yup.object().shape({
  StoreName: Yup.string()
    .required("field_required")
    .max(25, "store_name_max_25_characters"),
  KanaName: Yup.string()
    .required("field_required")
    .matches(stringJpRegexValidate, "only_kana_are_allowed")
    .max(25, "store_name_max_25_characters"),
  StoreOwnerName: Yup.string()
    .required("field_required")
    .max(25, "owner_name_max_25_characters"),
  BusinessTypeId: Yup.number()
    .required("field_required")
    .min(1, "field_required"),
  StoreCategory: Yup.string().oneOf(["0", "1"]).required("field_required"),
  Email: Yup.string()
    .required("email_is_required")
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address"),
  PostalCode: Yup.string()
    .matches(integerNumJpRegex, "only_numbers_are_allowed")
    .required("field_required")
    .length(7, "postal_code_max_7_characters"),
  Address: Yup.string()
    .required("field_required")
    .max(256, "max_256_characters")
    .test(
      "field_required_address",
      "field_required_address",
      function (value, context) {
        const orignal = value.replace(context?.parent?.PrefectureName, "");
        if (orignal.trim() == "") {
          return false;
        }
        return true;
      }
    ),
  MobileNumber: Yup.string()
    .matches(integerRegex, "only_numbers_are_allowed")
    .test("start-with-zero", "phone_number_must_start_with_0", (val) => {
      if (!val) return false;
      const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
      return digits.startsWith("0");
    })
    .test(
      "valid-japanese-phone",
      "telephone_10_or_11_length",
      (val) => {
        if (!val) return false;
        const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
        return digitsOnly.length === 10 || digitsOnly.length === 11;
      }
    )
    .required("field_required"),
  Status: Yup.string().oneOf(["0", "1"]).required("field_required"),
  IsNominationFee: Yup.string().oneOf(["0", "1"]).required("field_required"),
  StoreLink: Yup.string()
    .max(1024, "url_char_limit_reached")
    .matches(
      /^(https:\/\/(www\.)?|http:\/\/(www\.)?)[A-Za-z0-9-_.:/]+$/,
      "invalid_url"
    ),
  Url: Yup.string()
    .max(1024, "url_char_limit_reached")
    .url(
      "invalid_url"
    ),
  Password: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    ),
  // .required("field_required"),
  ConfirmPassword: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .oneOf([Yup.ref("Password")], "validation_new_confirm_password_mismatch")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    ),
  // .required("field_required"),
});

export const AddStaffValidationSchema = (IsOtherStaffEmail: boolean) => IsOtherStaffEmail ? Yup.object().shape({
  StoreId: Yup.number().required("field_required").min(1, "field_required"),
  StaffName: Yup.string()
    .required("staff_name_is_required")
    .max(30, "max_30_characters"),
  TelephoneNumber: Yup.string()
    .required("field_required")
    .matches(integerRegex, "only_numbers_are_allowed")
    .test("start-with-zero", "phone_number_must_start_with_0", (val) => {
      if (!val) return false;
      const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
      return digits.startsWith("0");
    })
    .test(
      "valid-japanese-phone",
      "telephone_10_or_11_length",
      (val) => {
        if (!val) return false;
        const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
        return digitsOnly.length === 10 || digitsOnly.length === 11;
      }
    ),
    // .matches(/^\+?[0-9\s-]+$/, "invalid_telephone_number"),
  Email: Yup.string()
    .required("email_is_required")
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address"),
  PostCode: Yup.string()
    // .required("postal_code_is_required")
    .matches(integerNumJpRegex, "invalid_postal_code")
    .length(7, "postal_code_max_7_characters"),

  Address: Yup.string()
    .max(256, "max_256_characters")
    .when("PostCode", {
      is: (postal: string) => !postal || postal.trim() === "",
      then: (schema) =>
        schema.test(
          "postal-required-when-address",
          "postal_code_is_required_when_address_is_filled",
          function (value) {
            return !(value && value.trim().length > 0);
          }
        ),
      otherwise: (schema) => schema,
    })
    .test(
      "field_required_address",
      "field_required_address",
      function (value, context) {
        if (!value) return true;

        const original = value.replace(context?.parent?.PrefectureName || "", "");
        return original.trim() !== "";
      }
    ),

  Expertise: Yup.string().max(60, "max_60_characters"),
  AccountStatus: Yup.string()
    .required("status_is_required")
    .oneOf(["Valid", "Invalid"], "invalid_status_value"),
  NumberOfAvailability: Yup.string()
    .required("availability_is_required")
    .matches(/^[1-9]\d*$/, "invalid_availability")
    .max(50, "availability_max_50_characters"),
  NominationFee: Yup.number()
    .typeError("nomination_fee_must_be_valid_number")
    .max(10000, "nomination_fee_max_10000"),
  // Password: Yup.string()
  //   .min(8, "min_length_required")
  //   .max(20, "min_length_required")
  //   .matches(
  //     /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
  //     "user_validation_password_matches"
  //   )
  //   .required("field_required"),
  // ConfirmPassword: Yup.string()
  //   .min(8, "min_length_required")
  //   .max(20, "min_length_required")
  //   .oneOf([Yup.ref("Password")], "validation_new_confirm_password_mismatch")
  //   .matches(
  //     /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
  //     "user_validation_password_matches"
  //   )
  //   .required("field_required"),
  Introduction: Yup.string().max(300, "max_300_characters"),
  ShowOnReservationSite: Yup.boolean()
    .required("status_is_required")
    .oneOf([true, false], "invalid_status_value"),
}) : Yup.object().shape({
  StoreId: Yup.number().required("field_required").min(1, "field_required"),
  StaffName: Yup.string()
    .required("staff_name_is_required")
    .max(30, "max_30_characters"),
  TelephoneNumber: Yup.string()
    .required("field_required")
    .matches(integerRegex, "only_numbers_are_allowed")
    .test("start-with-zero", "phone_number_must_start_with_0", (val) => {
      if (!val) return false;
      const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
      return digits.startsWith("0");
    })
    .test(
      "valid-japanese-phone",
      "telephone_10_or_11_length",
      (val) => {
        if (!val) return false;
        const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
        return digitsOnly.length === 10 || digitsOnly.length === 11;
      }
    ),
    // .matches(/^\+?[0-9\s-]+$/, "invalid_telephone_number"),
  Email: Yup.string()
    .required("email_is_required")
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address"),
  PostCode: Yup.string()
    // .required("postal_code_is_required")
    .matches(integerNumJpRegex, "invalid_postal_code")
    .length(7, "postal_code_max_7_characters"),

  Address: Yup.string()
    .max(256, "max_256_characters")
    .when("PostCode", {
      is: (postal: string) => !postal || postal.trim() === "",
      then: (schema) =>
        schema.test(
          "postal-required-when-address",
          "postal_code_is_required_when_address_is_filled",
          function (value) {
            return !(value && value.trim().length > 0);
          }
        ),
      otherwise: (schema) => schema,
    })
    .test(
      "field_required_address",
      "field_required_address",
      function (value, context) {
        if (!value) return true;

        const original = value.replace(context?.parent?.PrefectureName || "", "");
        return original.trim() !== "";
      }
    ),

  Expertise: Yup.string().max(60, "max_60_characters"),
  AccountStatus: Yup.string()
    .required("status_is_required")
    .oneOf(["Valid", "Invalid"], "invalid_status_value"),
  NumberOfAvailability: Yup.string()
    .required("availability_is_required")
    .matches(/^[1-9]\d*$/, "invalid_availability")
    .max(50, "availability_max_50_characters"),
  NominationFee: Yup.number()
    .typeError("nomination_fee_must_be_valid_number")
    .max(10000, "nomination_fee_max_10000"),
  Password: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .required("field_required"),
  ConfirmPassword: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .oneOf([Yup.ref("Password")], "validation_new_confirm_password_mismatch")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .required("field_required"),
  Introduction: Yup.string().max(300, "max_300_characters"),
  ShowOnReservationSite: Yup.boolean()
    .required("status_is_required")
    .oneOf([true, false], "invalid_status_value"),
});

export const EditStaffValidationSchema = (totalCount: number) => {
  return Yup.object().shape({
    StoreId: Yup.number().required("field_required").min(1, "field_required"),
    StaffName: Yup.string()
      .required("staff_name_is_required")
      .max(30, "max_30_characters"),
    TelephoneNumber: Yup.string()
      .required("field_required")
      // .test("len", "telephone_10_or_11_length", val => {
      //   const digitsOnly = val?.replace(/[^\d\uff10-\uff19]/g, "") || "";
      //   return digitsOnly.length === 10 || digitsOnly.length === 11;
      // })
      // .matches(/^\+?[0-9\s-]+$/, "invalid_telephone_number"),
      .matches(integerRegex, "only_numbers_are_allowed")
    .test("start-with-zero", "phone_number_must_start_with_0", (val) => {
      if (!val) return false;
      const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
      return digits.startsWith("0");
    })
    .test(
      "valid-japanese-phone",
      "telephone_10_or_11_length",
      (val) => {
        if (!val) return false;
        const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
        return digitsOnly.length === 10 || digitsOnly.length === 11;
      }
    ),
    Email: Yup.string()
      .required("email_is_required")
      .email("invalid_email_address")
      .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address"),
    PostCode: Yup.string()
      // .required("postal_code_is_required")
      .matches(integerNumJpRegex, "invalid_postal_code")
      .length(7, "postal_code_max_7_characters"),
    Address: Yup.string()
      .max(256, "max_256_characters")
      .when("PostCode", {
        is: (postal: string) => !postal || postal.trim() === "",
        then: (schema) =>
          schema.test(
            "postal-required-when-address",
            "postal_code_is_required_when_address_is_filled",
            function (value) {
              return !(value && value.trim().length > 0);
            }
          ),
        otherwise: (schema) => schema,
      })
      .test(
        "field_required_address",
        "field_required_address",
        function (value, context) {
          if (!value) return true;

          const original = value.replace(context?.parent?.PrefectureName || "", "");
          return original.trim() !== "";
        }
      ),
    AccountStatus: Yup.string()
      .required("status_is_required")
      .oneOf(["Valid", "Invalid"], "invalid_status_value"),
    Expertise: Yup.string().max(60, "max_60_characters"),
    NumberOfAvailability: Yup.string()
      .required("availability_is_required")
      .matches(/^[1-9]\d*$/, "invalid_availability")
      .max(50, "availability_max_50_characters"),
    NominationFee: Yup.number()
      .typeError("nomination_fee_must_be_valid_number")
      .max(10000, "nomination_fee_max_10000"),
    Introduction: Yup.string().max(300, "max_300_characters"),
    OrderId: Yup.number()
      .required("orderid_is_required")
      .min(1, "field_required")
      .test(
        "is-smaller",
        `${formatMessage("must_be_small_than_totalcount")} ${totalCount}`,
        function (value) {
          const { TotalCount } = this.parent;
          return value <= TotalCount;
        }
      ),
    ShowOnReservationSite: Yup.boolean()
      .required("status_is_required")
      .oneOf([true, false], "invalid_status_value"),
  });
};

export const ReservationFormExistingCustomerSchema = Yup.object().shape({
  selectedServices: Yup.array()
    .of(Yup.object())
    .min(1, formatMessage("please_choose_a_service")),

  selectedCustomer: Yup.string().when("IsNewCustomer", (value, schema) => {
    if (value[0] == false) {
      return schema
        .notOneOf(["-1"], formatMessage("please_choose_a_customer"))
        .required(formatMessage("field_required"));
    } else {
      return schema.nullable().notRequired();
    }
  }),

  FirstNameKana: Yup.string().when("IsNewCustomer", (value, schema) => {
    if (value[0] == true) {
      return schema.required(formatMessage('field_required')).matches(stringJpRegexValidate, formatMessage("only_kana_are_allowed")).max(30, formatMessage("max_30_characters"));
    } else {
      return schema.nullable().notRequired();
    }
  }),
  LastNameKana: Yup.string().when("IsNewCustomer", (value, schema) => {
    if (value[0] == true) {
      return schema.required(formatMessage('field_required')).matches(stringJpRegexValidate, formatMessage("only_kana_are_allowed")).max(30, formatMessage("max_30_characters"));
    } else {
      return schema.nullable().notRequired();
    }
  }),
  Email: Yup.string().when("IsNewCustomer", (value, schema) => {
    if (value[0] == true) {
      return schema
        // .required(formatMessage('field_required'))
        .email(formatMessage("invalid_email_address"))
        .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, formatMessage("invalid_email_address"));
    } else {
      return schema.nullable().notRequired();
    }
  }),

  Phone: Yup.string().when("IsNewCustomer", (value, schema) => {
    if (value[0] == true) {
      return schema
        // .matches(integerRegex, formatMessage("only_digits"))
        // .test("len", formatMessage("telephone_10_or_11_length"), val => {
        //   const digitsOnly = val?.replace(/[^\d\uff10-\uff19]/g, "") || "";
        //   return digitsOnly.length === 10 || digitsOnly.length === 11;
        // })
        .matches(integerRegex, formatMessage("only_numbers_are_allowed"))
        .test("start-with-zero", formatMessage("phone_number_must_start_with_0"), (val) => {
          if (!val) return false;
          const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
          return digits.startsWith("0");
        })
        .test(
          "valid-japanese-phone",
          formatMessage("telephone_10_or_11_length"),
          (val) => {
            if (!val) return false;
            const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
            return digitsOnly.length === 10 || digitsOnly.length === 11;
          }
        )
        .required(formatMessage('field_required'));
    } else {
      return schema.nullable().notRequired();
    }
  }),
});
export const SMRAddTreatmentFormSchema = Yup.object().shape({
  selectedServices: Yup.array()
    .of(Yup.object())
    .min(1, formatMessage("please_choose_a_treatment")),
});


export const AddHeadquarterValidationSchema = Yup.object().shape({
  BusinessTypeId: Yup.number()
    .required("field_required")
    .min(1, "field_required"),
  HeadquarterName: Yup.string()
    .required("field_required")
    .max(30, "max_30_characters"),
  Email: Yup.string()
    .required("email_is_required")
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address"),
  Password: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .required("field_required"),
  ConfirmPassword: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .oneOf([Yup.ref("Password")], "validation_new_confirm_password_mismatch")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .required("field_required"),
});

export const AddStaffBreakValidationSchema = Yup.object().shape({
  Title: Yup.string().required("field_required").max(30, "max_30_characters"),
  StaffId: Yup.number().required("field_required").min(1, "field_required"),
  StartTime: Yup.string().required("start_time_is_required")
    .test("is-after-midnight", "start_time_is_required", function (value) {
      let isValid = (value && value > "00:00") ? true : false
      return isValid;
    }),
  EndTime: Yup.string()
    .required("end_time_is_required")
    .test(
      "is-required",
      "end_time_is_required",
      function (value) {
        return value.toString() !== "00:00";
      }
    )
    .test(
      "is-after-start",
      "end_time_should_be_after_start_time",
      function (value) {
        const { StartTime } = this.parent;
        return !StartTime || !value || StartTime < value;
      }
    ),
});

export const RebookReservationValidationSchema = (reservationDetail: any) =>
  Yup.object().shape({
    Date: Yup.string().required("field_required"),
    Time: Yup.string().test(
      "is-valid-time",
      "outside_business_hours",
      (value, context) => {
        let reservation = reservationDetail?.BusinessSettings;
        let date = context.options?.context?.Date;
        if (value === null || value === undefined) {
          return false;
        } else {
          return !isTimeOuterThanShift(reservationDetail, value, date);
        }
      }
    ),
  });


export const EditHeadquarterValidationSchema = Yup.object().shape({
  BusinessTypeId: Yup.number()
    .required("field_required")
    .min(1, "field_required"),
  HeadquarterName: Yup.string()
    .required("field_required")
    .max(30, "max_30_characters"),
  Email: Yup.string()
    .required("email_is_required")
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address")
});
const getPlainTextLength = (html: string): number => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  const plainText = tempDiv.textContent || tempDiv.innerText || '';
  return plainText.replace(/\s+/g, ' ').trim().length;
};

export const AddAgreementValidationSchema = Yup.object().shape({
  BusinessTypeId: Yup.number().required("field_required").min(1, "field_required"),
  AgreementName: Yup.string()
    .required("field_required")
    .max(30, "max_30_characters"),
  Content: Yup.string()
    .test('required', 'field_required', (value) => {
      const plainTextLength = getPlainTextLength(value || '');
      return plainTextLength >= 1;
    })
    .test('maxLength', 'max_5000_characters', (value) => {
      const plainTextLength = getPlainTextLength(value || '');
      return plainTextLength <= 5000;
    })
    .required('field_required'),
});

export const EditAgreementValidationSchema = Yup.object().shape({
  BusinessTypeId: Yup.number().required("field_required").min(1, "field_required"),
  Content: Yup.string()
    .test('required', 'field_required', (value) => {
      const plainTextLength = getPlainTextLength(value || '');
      return plainTextLength >= 1;
    })
    .test('maxLength', 'max_5000_characters', (value) => {
      const plainTextLength = getPlainTextLength(value || '');
      return plainTextLength <= 5000;
    })
    .required('field_required'),
});



export const AddTreatmentValidationSchema = (selectedCategory: any) =>
  Yup.object().shape({
    BusinessTypeId: Yup.number()
      .required("field_required")
      .min(1, "field_required"),
    // CategoryId: Yup.number().required("field_required").min(1, "field_required"),
    CategoryPath: Yup.string().test("maxDigits", "field_required", (value) => {
      if (selectedCategory) {
        return true;
      }
      return false;
    }),
    TreatmentName: Yup.string()
      .required("field_required")
      .max(120, "max_120_characters"),
    Time: Yup.number().required("field_required").min(1, "field_required"),
    FeeIncludingTax: Yup.mixed()
      .required("field_required")
      .test("is-number", "fee_must_be_a_number", value => {
        return !isNaN(Number(toHalfWidth(value)));
      })
      .test("is-positive", "fee_must_be_positive_number", value => {
        return Number(toHalfWidth(value)) >= 0;
      })
      .test("max-limit", "fee_must_be_less_than_999999999", value => {
        return Number(toHalfWidth(value)) <= 999999999;
      }),
    IsReservationSite: Yup.boolean()
      .required("reservation_site_is_required")
      .oneOf([true, false], "invalid_option_for_reservation_site"),
    TaxId: Yup.number().required("field_required").min(1, "field_required"),
    Status: Yup.string()
      .required("status_is_required")
      .oneOf(["1", "0"], "invalid_option_for_status"),
    StartDate: Yup.date()
      .nullable()
      .typeError("invalid_date_format")
      .test(
        "valid-start-date",
        "invalid_date_format",
        function (value) {
          return value === null || dayjs(value).isValid();
        }
      )
      .test(
        "start-date-required-if-end-date-exists",
        "start_date_is_required_if_end_date_is_provided",
        function (value) {
          const endDate = this.parent.EndDate;
          return !endDate || (dayjs(value).isValid() && !!value);
        }
      ),
    EndDate: Yup.date()
      .nullable()
      .typeError("invalid_date_format")
      .test(
        "valid-end-date",
        "invalid_date_format",
        function (value) {
          return value === null || dayjs(value).isValid();
        }
      )
      .test(
        "end-date-required-if-start-date-exists",
        "end_date_is_required_if_start_date_is_provided",
        function (value) {
          const startDate = this.parent.StartDate;
          return !startDate || (dayjs(value).isValid() && !!value);
        }
      )
      .test(
        "end-date-must-be-after-start-date",
        "end_date_must_be_same_or_after_start_date",
        function (value) {
          const startDate = this.parent.StartDate;
          return !startDate || !value || (dayjs(value).isValid() && dayjs(startDate).isValid() && dayjs(value).isSameOrAfter(dayjs(startDate)));
        }
      ),


    Note: Yup.string().max(300, "max_300_characters"),
    Description: Yup.string().max(300, "max_300_characters"),
  });
export const AddStoreTreatmentValidationSchema = (selectedCategory: any) =>
  Yup.object().shape({
    BusinessTypeId: Yup.number()
      .required("field_required")
      .min(1, "field_required"),
    // CategoryId: Yup.number().required("field_required").min(1, "field_required"),
    CategoryPath: Yup.string().test("maxDigits", "field_required", (value) => {
      if (selectedCategory) {
        return true;
      }
      return false;
    }),
    TreatmentName: Yup.string()
      .required("field_required")
      .max(120, "max_120_characters"),
    Time: Yup.number().required("field_required").min(1, "field_required"),
    FeeIncludingTax: Yup.mixed()
      .required("field_required")
      .test("is-number", "fee_must_be_a_number", value => {
        return !isNaN(Number(toHalfWidth(value)));
      })
      .test("is-positive", "fee_must_be_positive_number", value => {
        return Number(toHalfWidth(value)) >= 0;
      })
      .test("max-limit", "fee_must_be_less_than_999999999", value => {
        return Number(toHalfWidth(value)) <= 999999999;
      }),
    IsReservationSite: Yup.boolean()
      .required("reservation_site_is_required")
      .oneOf([true, false], "invalid_option_for_reservation_site"),
    TaxId: Yup.number().required("field_required").min(1, "field_required"),
    Status: Yup.string()
      .required("status_is_required")
      .oneOf(["1", "0"], "invalid_option_for_status"),
    StartDate: Yup.date()
      .nullable()
      .typeError("invalid_date_format")
      .test(
        "valid-start-date",
        "invalid_date_format",
        function (value) {
          return value === null || dayjs(value).isValid();
        }
      )
      .test(
        "start-date-required-if-end-date-exists",
        "start_date_is_required_if_end_date_is_provided",
        function (value) {
          const endDate = this.parent.EndDate;
          return !endDate || (dayjs(value).isValid() && !!value);
        }
      ),
    EndDate: Yup.date()
      .nullable()
      .typeError("invalid_date_format")
      .test(
        "valid-end-date",
        "invalid_date_format",
        function (value) {
          return value === null || dayjs(value).isValid();
        }
      )
      .test(
        "end-date-required-if-start-date-exists",
        "end_date_is_required_if_start_date_is_provided",
        function (value) {
          const startDate = this.parent.StartDate;
          return !startDate || (dayjs(value).isValid() && !!value);
        }
      )
      .test(
        "end-date-must-be-after-start-date",
        "end_date_must_be_same_or_after_start_date",
        function (value) {
          const startDate = this.parent.StartDate;
          return !startDate || !value || (dayjs(value).isValid() && dayjs(startDate).isValid() && dayjs(value).isSameOrAfter(dayjs(startDate)));
        }
      ),


    Note: Yup.string().max(300, "max_300_characters"),
    Description: Yup.string().max(300, "max_300_characters"),
    //Format Message is used here because we are using the Error Message component of formik library
    StaffOptions: Yup.array().required(formatMessage("field_required"))
      .min(1, formatMessage("field_required"))
  });

export const EditTreatmentValidationSchema = (selectedCategory: any) =>
  Yup.object().shape({
    BusinessTypeId: Yup.number()
      .required("field_required")
      .min(1, "field_required"),
    // CategoryId: Yup.number().required("field_required").min(1, "field_required"),
    CategoryPath: Yup.string().test("maxDigits", "field_required", (value) => {
      if (selectedCategory) {
        return true;
      }
      return false;
    }),
    TreatmentName: Yup.string()
      .required("field_required")
      .max(120, "max_120_characters"),
    Time: Yup.number().required("field_required").min(1, "field_required"),
    FeeIncludingTax: Yup.mixed()
      .required("field_required")
      .test("is-number", "fee_must_be_a_number", value => {
        return !isNaN(Number(toHalfWidth(value)));
      })
      .test("is-positive", "fee_must_be_positive_number", value => {
        return Number(toHalfWidth(value)) >= 0;
      })
      .test("max-limit", "fee_must_be_less_than_999999999", value => {
        return Number(toHalfWidth(value)) <= 999999999;
      }),
    IsReservationSite: Yup.boolean()
      .required("reservation_site_is_required")
      .oneOf([true, false], "invalid_option_for_reservation_site"),
    TaxId: Yup.number().required("field_required").min(1, "field_required"),
    Status: Yup.string()
      .required("status_is_required")
      .oneOf(["1", "0"], "invalid_option_for_status"),
    Note: Yup.string().max(300, "max_300_characters"),
    Description: Yup.string().max(300, "max_300_characters"),
    StartDate: Yup.date()
      .nullable()
      .typeError("invalid_date_format")
      .test(
        "valid-start-date",
        "invalid_date_format",
        function (value) {
          return value === null || dayjs(value).isValid();
        }
      )
      .test(
        "start-date-required-if-end-date-exists",
        "start_date_is_required_if_end_date_is_provided",
        function (value) {
          const endDate = this.parent.EndDate;
          return !endDate || (dayjs(value).isValid() && !!value);
        }
      ),
    EndDate: Yup.date()
      .nullable()
      .typeError("invalid_date_format")
      .test(
        "valid-end-date",
        "invalid_date_format",
        function (value) {
          return value === null || dayjs(value).isValid();
        }
      )
      .test(
        "end-date-required-if-start-date-exists",
        "end_date_is_required_if_start_date_is_provided",
        function (value) {
          const startDate = this.parent.StartDate;
          return !startDate || (dayjs(value).isValid() && !!value);
        }
      )
      .test(
        "end-date-must-be-after-start-date",
        "end_date_must_be_same_or_after_start_date",
        function (value) {
          const startDate = this.parent.StartDate;
          return !startDate || !value || (dayjs(value).isValid() && dayjs(startDate).isValid() && dayjs(value).isSameOrAfter(dayjs(startDate)));
        }
      ),
  
  });
export const EditStoreTreatmentValidationSchema = (selectedCategory: any) =>
  Yup.object().shape({
    BusinessTypeId: Yup.number()
      .required("field_required")
      .min(1, "field_required"),
    // CategoryId: Yup.number().required("field_required").min(1, "field_required"),
    CategoryPath: Yup.string().test("maxDigits", "field_required", (value) => {
      if (selectedCategory) {
        return true;
      }
      return false;
    }),
    TreatmentName: Yup.string()
      .required("field_required")
      .max(120, "max_120_characters"),
    Time: Yup.number().required("field_required").min(1, "field_required"),
    FeeIncludingTax: Yup.mixed()
      .required("field_required")
      .test("is-number", "fee_must_be_a_number", value => {
        return !isNaN(Number(toHalfWidth(value)));
      })
      .test("is-positive", "fee_must_be_positive_number", value => {
        return Number(toHalfWidth(value)) >= 0;
      })
      .test("max-limit", "fee_must_be_less_than_999999999", value => {
        return Number(toHalfWidth(value)) <= 999999999;
      }),
    IsReservationSite: Yup.boolean()
      .required("reservation_site_is_required")
      .oneOf([true, false], "invalid_option_for_reservation_site"),
    TaxId: Yup.number().required("field_required").min(1, "field_required"),
    Status: Yup.string()
      .required("status_is_required")
      .oneOf(["1", "0"], "invalid_option_for_status"),
    Note: Yup.string().max(300, "max_300_characters"),
    Description: Yup.string().max(300, "max_300_characters"),
    StartDate: Yup.date()
      .nullable()
      .typeError("invalid_date_format")
      .test(
        "valid-start-date",
        "invalid_date_format",
        function (value) {
          return value === null || dayjs(value).isValid();
        }
      )
      .test(
        "start-date-required-if-end-date-exists",
        "start_date_is_required_if_end_date_is_provided",
        function (value) {
          const endDate = this.parent.EndDate;
          return !endDate || (dayjs(value).isValid() && !!value);
        }
      ),
    EndDate: Yup.date()
      .nullable()
      .typeError("invalid_date_format")
      .test(
        "valid-end-date",
        "invalid_date_format",
        function (value) {
          return value === null || dayjs(value).isValid();
        }
      )
      .test(
        "end-date-required-if-start-date-exists",
        "end_date_is_required_if_start_date_is_provided",
        function (value) {
          const startDate = this.parent.StartDate;
          return !startDate || (dayjs(value).isValid() && !!value);
        }
      )
      .test(
        "end-date-must-be-after-start-date",
        "end_date_must_be_same_or_after_start_date",
        function (value) {
          const startDate = this.parent.StartDate;
          return !startDate || !value || (dayjs(value).isValid() && dayjs(startDate).isValid() && dayjs(value).isSameOrAfter(dayjs(startDate)));
        }
      ),
    //Format Message is used here because we are using the Error Message component of formik library
    StaffOptions: Yup.array().required(formatMessage("field_required"))
      .min(1, formatMessage("field_required"))
  
  });

export const AddProductValidationSchema = (selectedCategory: any) =>
  Yup.object().shape({
    BusinessTypeId: Yup.number()
      .required("field_required")
      .min(1, "field_required"),
    CategoryPath: Yup.string().test("maxDigits", "field_required", (value) => {
      if (selectedCategory) {
        return true;
      }
      return false;
    }),
    // CategoryId: Yup.number().required("field_required").min(1, "field_required"),
    TaxId: Yup.number().required("field_required").min(1, "field_required"),
    UnitPriceTaxId: Yup.number().required("field_required").min(1, "field_required"),
    ProductName: Yup.string()
      .required("field_required")
      .max(120, "max_120_characters"),
    PriceIncludingTax: Yup.mixed()
      .required("field_required")
      .test("is-number", "fee_must_be_a_number", value => {
        return !isNaN(Number(toHalfWidth(value)));
      })
      .test("is-positive", "fee_must_be_positive_number", value => {
        return Number(toHalfWidth(value)) >= 0;
      })
      .test("max-limit", "fee_must_be_less_than_999999999", value => {
        return Number(toHalfWidth(value)) <= 999999999;
      }),
    UnitPrice: Yup.mixed()
      .required("field_required")
      .test("is-number", "fee_must_be_a_number", value => {
        return !isNaN(Number(toHalfWidth(value)));
      })
      .test("is-positive", "fee_must_be_positive_number", value => {
        return Number(toHalfWidth(value)) >= 0;
      })
      .test("max-limit", "fee_must_be_less_than_999999999", value => {
        return Number(toHalfWidth(value)) <= 999999999;
      }),
    StartDate: Yup.date()
      .nullable()
      .typeError("invalid_date_format")
      .test(
        "valid-start-date",
        "invalid_date_format",
        function (value) {
          return value === null || dayjs(value).isValid();
        }
      )
      .test(
        "start-date-required-if-end-date-exists",
        "start_date_is_required_if_end_date_is_provided",
        function (value) {
          const endDate = this.parent.EndDate;
          return !endDate || (dayjs(value).isValid() && !!value);
        }
      ),
    EndDate: Yup.date()
      .nullable()
      .typeError("invalid_date_format")
      .test(
        "valid-end-date",
        "invalid_date_format",
        function (value) {
          return value === null || dayjs(value).isValid();
        }
      )
      .test(
        "end-date-required-if-start-date-exists",
        "end_date_is_required_if_start_date_is_provided",
        function (value) {
          const startDate = this.parent.StartDate;
          return !startDate || (dayjs(value).isValid() && !!value);
        }
      )
      .test(
        "end-date-must-be-after-start-date",
        "end_date_must_be_same_or_after_start_date",
        function (value) {
          const startDate = this.parent.StartDate;
          return !startDate || !value || (dayjs(value).isValid() && dayjs(startDate).isValid() && dayjs(value).isSameOrAfter(dayjs(startDate)));
        }
      ),

    Note: Yup.string().max(300, "max_300_characters"),
    Description: Yup.string().max(300, "max_300_characters"),
    BarCode: Yup.string()
      .matches(/^[^\uFF01-\uFF60\uFFE0-\uFFE6]*$/, "barcode_invalid_full_width")
      .min(8, "barcode_char_limit_reached")
      .max(13, "barcode_char_limit_reached"),
  });

export const EditProductValidationSchema = (selectedCategory: any) =>
  Yup.object().shape({
    BusinessTypeId: Yup.number()
      .required("field_required")
      .min(1, "field_required"),
    CategoryPath: Yup.string().test("maxDigits", "field_required", (value) => {
      if (selectedCategory) {
        return true;
      }
      return false;
    }),
    // CategoryId: Yup.number().required("field_required").min(1, "field_required"),
    TaxId: Yup.number().required("field_required").min(1, "field_required"),
    UnitPriceTaxId: Yup.number().required("field_required").min(1, "field_required"),
    ProductName: Yup.string().required("field_required").max(120, "max_120_characters"),
    PriceIncludingTax: Yup.mixed()
      .required("field_required")
      .test("is-number", "fee_must_be_a_number", value => {
        return !isNaN(Number(toHalfWidth(value)));
      })
      .test("is-positive", "fee_must_be_positive_number", value => {
        return Number(toHalfWidth(value)) >= 0;
      })
      .test("max-limit", "fee_must_be_less_than_999999999", value => {
        return Number(toHalfWidth(value)) <= 999999999;
      }),
    UnitPrice: Yup.mixed()
      .required("field_required")
      .test("is-number", "fee_must_be_a_number", value => {
        return !isNaN(Number(toHalfWidth(value)));
      })
      .test("is-positive", "fee_must_be_positive_number", value => {
        return Number(toHalfWidth(value)) >= 0;
      })
      .test("max-limit", "fee_must_be_less_than_999999999", value => {
        return Number(toHalfWidth(value)) <= 999999999;
      }),
    Note: Yup.string().max(300, "max_300_characters"),
    Description: Yup.string().max(300, "max_300_characters"),
    StartDate: Yup.date()
      .nullable()
      .typeError("invalid_date_format")
      .test(
        "valid-start-date",
        "invalid_date_format",
        function (value) {
          return value === null || dayjs(value).isValid();
        }
      )
      .test(
        "start-date-required-if-end-date-exists",
        "start_date_is_required_if_end_date_is_provided",
        function (value) {
          const endDate = this.parent.EndDate;
          return !endDate || (dayjs(value).isValid() && !!value);
        }
      ),
    EndDate: Yup.date()
      .nullable()
      .typeError("invalid_date_format")
      .test(
        "valid-end-date",
        "invalid_date_format",
        function (value) {
          return value === null || dayjs(value).isValid();
        }
      )
      .test(
        "end-date-required-if-start-date-exists",
        "end_date_is_required_if_start_date_is_provided",
        function (value) {
          const startDate = this.parent.StartDate;
          return !startDate || (dayjs(value).isValid() && !!value);
        }
      )
      .test(
        "end-date-must-be-after-start-date",
        "end_date_must_be_same_or_after_start_date",
        function (value) {
          const startDate = this.parent.StartDate;
          return !startDate || !value || (dayjs(value).isValid() && dayjs(startDate).isValid() && dayjs(value).isSameOrAfter(dayjs(startDate)));
        }
      ),
    BarCode: Yup.string()
      .matches(/^[^\uFF01-\uFF60\uFFE0-\uFFE6]*$/, "barcode_invalid_full_width")
      .min(8, "barcode_char_limit_reached")
      .max(13, "barcode_char_limit_reached"),
    //BarCode: Yup.string().required("field_required")
  });

export const AddCategoryValidationSchema = Yup.object().shape({
  Category: Yup.string()
    .required("field_required")
    .max(40, "category_max_40_characters"),
  Description: Yup.string().max(40, "description_max_40_characters").nullable(),
});

export const EditCategoryValidationSchema = Yup.object().shape({
  Category: Yup.string()
    .required("category_is_required")
    .min(2, "category_min_2_characters")
    .max(40, "category_max_40_characters"),
  distinction: Yup.string()
    .required("distinction_is_required")
    .oneOf(["treatment", "merchandise"], "invalid_distinction_value"),
  BusinessType: Yup.string().required("business_type_is_required"),
  parentCategory: Yup.string().required("parent_category_is_required"),
  Description: Yup.string().max(40, "description_max_40_characters"),
});
export const AddShiftValidationSchema = Yup.object().shape({
  ShiftName: Yup.string()
    .required("shift_name_is_required")
    .min(2, "shift_name_min_2_characters")
    .max(15, "shift_max_15_characters"),
  ShiftShortName: Yup.string()
    .required("shift_short_name_is_required")
    // .min(2, "shift_short_name_min_2_characters")
    .max(5, "shift_short_name_max_5_characters"),
  StartTime: Yup.string().required("start_time_is_required")
    .test("is-after-midnight", "start_time_is_required", function (value) {
      let isValid = (value && value > "00:00") ? true : false
      return isValid;
    }),
  EndTime: Yup.string()
    .required("end_time_is_required")
    .test(
      "is-required",
      "end_time_is_required",
      function (value) {
        return value.toString() !== "00:00";
      }
    )
    .test(
      "is-after-start",
      "end_time_should_be_after_start_time",
      function (value) {
        const { StartTime } = this.parent;
        return !StartTime || !value || StartTime < value;
      }
    ),
  Colour: Yup.string()
    .required("color_is_required")
    .matches(/^#(?!FFFFFF|ffffff)[0-9A-Fa-f]{6}$/, "invalid_color_code"),
  Memo: Yup.string().max(300, "memo_max_300_characters"),
});

export const EditShiftValidationSchema = Yup.object().shape({
  ShiftName: Yup.string()
    .required("shift_name_is_required")
    .min(2, "shift_name_min_2_characters")
    .max(15, "shift_max_15_characters"),
  ShiftShortName: Yup.string()
    .required("shift_short_name_is_required")
    // .min(2, "shift_short_name_min_2_characters")
    .max(5, "shift_short_name_max_5_characters"),
  StartTime: Yup.string().required("start_time_is_required")
    .test("is-after-midnight", "start_time_is_required", function (value) {
      let isValid = (value && value > "00:00") ? true : false
      return isValid;
    }),
  EndTime: Yup.string()
    .required("end_time_is_required")
    .test(
      "is-required",
      "end_time_is_required",
      function (value) {
        return value.toString() !== "00:00";
      }
    )
    .test(
      "is-after-start",
      "end_time_should_be_after_start_time",
      function (value) {
        const { StartTime } = this.parent;
        return !StartTime || !value || StartTime < value;
      }
    ),
  Colour: Yup.string()
    .required("color_is_required")
    .matches(/^#(?!FFFFFF|ffffff)[0-9A-Fa-f]{6}$/, "invalid_color_code"),
  Memo: Yup.string().max(300, "memo_max_300_characters"),
});
export const PublishScheduleValidationSchema = (
  publishDateRange: { startDate: Date | null; endDate: Date | null; },
  selectedOption: number,
  getClosedDays: any[],
  getReservationSettingDetail: any
) => {
  // Helper function to check if a date is valid
  const isValidDate = (date: any): boolean => {
    if (!date) return false;
    const d = new Date(date);
    return d instanceof Date && !isNaN(d.getTime());
  };

  if (publishDateRange.startDate && publishDateRange.endDate) {
    if (selectedOption === 1 && getClosedDays) {
      const initialDate = new Date(publishDateRange.startDate).setHours(0, 0, 0, 0);
      const lastDate = new Date(publishDateRange.endDate).setHours(23, 59, 59, 999);
      const nextDateAfterLast = new Date(publishDateRange.endDate);
      nextDateAfterLast.setDate(nextDateAfterLast.getDate() + 1);
      nextDateAfterLast.setHours(0, 0, 0, 0);
      const nextValidStartDate = (() => {
        const date = new Date(publishDateRange.endDate);
        date.setDate(date.getDate() + 1);
        date.setHours(0, 0, 0, 0);
        while (
          getClosedDays.includes(date.getDay()) ||
          IsStoreClosedByDateForShift(getReservationSettingDetail, date)
        ) {
          date.setDate(date.getDate() + 1);
        }
        return date;
      })();

      return Yup.object().shape({
        StartDate: Yup.date()
          .typeError("invalid_date_format")
          .required("start_date_is_required")
          .test(
            "is-valid-date",
            "invalid_date_format",
            function (value) {
              return isValidDate(value);
            }
          )
          .test(
            "outside-initial-last-range",
            "date_range_has_published_shifts",
            function (value) {
              if (!value || !isValidDate(value)) return false;
              const date = new Date(value).getTime();
              return date < initialDate || date > lastDate;
            }
          )
          .test(
            "must-be-next-open-day",
            "start_date_must_be_next_open_day_after_last_published_date",
            function (value) {
              if (!value || !isValidDate(value)) return false;
              const date = new Date(value).setHours(0, 0, 0, 0);
              return date === nextValidStartDate.getTime();
            }
          ),

        EndDate: Yup.date()
          .typeError("invalid_date_format")
          .required("end_date_is_required")
          .test(
            "is-valid-date",
            "invalid_date_format",
            function (value) {
              return isValidDate(value);
            }
          )
          .test(
            "outside-initial-last-range",
            "date_range_has_published_shifts",
            function (value) {
              if (!value || !isValidDate(value)) return false;
              const date = new Date(value).getTime();
              return date < initialDate || date > lastDate;
            }
          )
          .test(
            "is-after-start",
            "end_date_must_be_same_or_later_than_start_date",
            function (value) {
              const { StartDate } = this.parent;
              if (!isValidDate(StartDate) || !isValidDate(value)) return false;
              return (
                StartDate &&
                value &&
                new Date(StartDate).setHours(0, 0, 0, 0) <=
                  new Date(value).setHours(0, 0, 0, 0)
              );
            }
          ),
      });
    } else {
      const publishedStart = publishDateRange.startDate
        ? new Date(publishDateRange.startDate).setHours(0, 0, 0, 0)
        : null;
      const publishedEnd = publishDateRange.endDate
        ? new Date(publishDateRange.endDate).setHours(23, 59, 59, 999)
        : null;

      return Yup.object().shape({
        StartDate: Yup.date()
          .typeError("invalid_date_format")
          .required("start_date_is_required")
          .test(
            "is-valid-date",
            "invalid_date_format",
            function (value) {
              return isValidDate(value);
            }
          )
          .test(
            "start-inside-range",
            "start_date_must_be_within_published_range",
            function (value) {
              if (!value || !isValidDate(value) || !publishedStart || !publishedEnd)
                return false;
              const date = new Date(value).setHours(0, 0, 0, 0);
              return date >= publishedStart && date <= publishedEnd;
            }
          ),

        EndDate: Yup.date()
          .typeError("invalid_date_format")
          .required("end_date_is_required")
          .test(
            "is-valid-date",
            "invalid_date_format",
            function (value) {
              return isValidDate(value);
            }
          )
          .test(
            "must-equal-last-published",
            "end_date_must_equal_last_published_date",
            function (value) {
              if (!value || !isValidDate(value) || !publishedEnd) return false;
              const date = new Date(value).setHours(0, 0, 0, 0);
              return date === new Date(publishedEnd).setHours(0, 0, 0, 0);
            }
          )
          .test(
            "is-after-start",
            "end_date_must_be_same_or_later_than_start_date",
            function (value) {
              const { StartDate } = this.parent;
              if (!isValidDate(StartDate) || !isValidDate(value)) return false;
              return (
                StartDate &&
                value &&
                new Date(StartDate).setHours(0, 0, 0, 0) <=
                  new Date(value).setHours(0, 0, 0, 0)
              );
            }
          ),
      });
    }
  }

  return Yup.object().shape({
    StartDate: Yup.date()
      .typeError("invalid_date_format")
      .required("start_date_is_required")
      .test(
        "is-valid-date",
        "invalid_date_format",
        function (value) {
          return isValidDate(value);
        }
      )
      .min(
        new Date(new Date().setHours(0, 0, 0, 0)),
        "start_date_must_be_today_or_later"
      ),
    EndDate: Yup.date()
      .typeError("invalid_date_format")
      .required("end_date_is_required")
      .test(
        "is-valid-date",
        "invalid_date_format",
        function (value) {
          return isValidDate(value);
        }
      )
      .test(
        "is-after-start",
        "end_date_must_be_same_or_later_than_start_date",
        function (value) {
          const { StartDate } = this.parent;
          if (!isValidDate(StartDate) || !isValidDate(value)) return false;
          return (
            StartDate &&
            value &&
            new Date(StartDate).setHours(0, 0, 0, 0) <=
              new Date(value).setHours(0, 0, 0, 0)
          );
        }
      ),
  });
};
export const FreeShiftValidationSchema = Yup.object().shape({
  FreeStartTime: Yup.string().required("start_time_is_required")
    .test("is-after-midnight", "start_time_is_required", function (value) {
      let isValid = (value && value > "00:00") ? true : false
      return isValid;
    }),
  FreeEndTime: Yup.string()
    .required("end_time_is_required")
    .test(
      "is-after-start",
      "end_time_should_be_after_start_time",
      function (value) {
        const { FreeStartTime } = this.parent;
        return !FreeStartTime || !value || FreeStartTime < value;
      }
    ),
});
export const ChangeShiftValidationSchema = Yup.object().shape({
  Shift: Yup.number().required("field_required").min(1, "field_required")
});

export const AddEditBusinessTypeValidationSchema = Yup.object().shape({
  BusinessType: Yup.string()
    .required("business_type_is_required")
    .min(1, "business_type_min_1_characters")
    .max(20, "business_type_max_20_characters"),
  Description: Yup.string().max(300, "max_300_characters"),
});


export const AddOptionMenuSchema = Yup.object().shape({
  BusinessTypeId: Yup.number()
    .required("field_required")
    .min(1, "field_required"),
  Treatment: Yup.array()
    .min(1, "at_least_one_treatment_must_be_selected")
    .required("field_required"),
  OptionName: Yup.string()
    .required("field_required")
    .max(120, "max_120_characters"),
  TimeId: Yup.number().required("field_required").min(1, "field_required"),
  FeeIncludingTax: Yup.mixed()
    .required("field_required")
    .test("is-number", "fee_must_be_a_number", value => {
      return !isNaN(Number(toHalfWidth(value)));
    })
    .test("is-positive", "fee_must_be_positive_number", value => {
      return Number(toHalfWidth(value)) >= 0;
    })
    .test("max-limit", "fee_must_be_less_than_999999999", value => {
      return Number(toHalfWidth(value)) <= 999999999;
    }),
  TaxId: Yup.number().required("field_required").min(1, "field_required"),
  Description: Yup.string().max(300, "max_300_characters"),
  Remarks: Yup.string().max(300, "max_300_characters"),
  StartDate: Yup.date()
    .nullable()
    .typeError("invalid_date_format")
    .test(
      "valid-start-date",
      "invalid_date_format",
      function (value) {
        return value === null || dayjs(value).isValid();
      }
    )
    .test(
      "start-date-required-if-end-date-exists",
      "start_date_is_required_if_end_date_is_provided",
      function (value) {
        const endDate = this.parent.EndDate;
        return !endDate || (dayjs(value).isValid() && !!value);
      }
    ),
  EndDate: Yup.date()
    .nullable()
    .typeError("invalid_date_format")
    .test(
      "valid-end-date",
      "invalid_date_format",
      function (value) {
        return value === null || dayjs(value).isValid();
      }
    )
    .test(
      "end-date-required-if-start-date-exists",
      "end_date_is_required_if_start_date_is_provided",
      function (value) {
        const startDate = this.parent.StartDate;
        return !startDate || (dayjs(value).isValid() && !!value);
      }
    )
    .test(
      "end-date-must-be-after-start-date",
      "end_date_must_be_same_or_after_start_date",
      function (value) {
        const startDate = this.parent.StartDate;
        return !startDate || !value || (dayjs(value).isValid() && dayjs(startDate).isValid() && dayjs(value).isSameOrAfter(dayjs(startDate)));
      }
    ),

});

export const EditOptionMenuSchema = Yup.object().shape({
  BusinessTypeId: Yup.number()
    .required("field_required")
    .min(1, "field_required"),
  Treatment: Yup.array()
    .min(1, "at_least_one_treatment_must_be_selected")
    .required("field_required"),
  OptionName: Yup.string()
    .required("field_required")
    .max(120, "max_120_characters"),
  TimeId: Yup.number().required("field_required").min(1, "field_required"),
  FeeIncludingTax: Yup.mixed()
    .required("field_required")
    .test("is-number", "fee_must_be_a_number", value => {
      return !isNaN(Number(toHalfWidth(value)));
    })
    .test("is-positive", "fee_must_be_positive_number", value => {
      return Number(toHalfWidth(value)) >= 0;
    })
    .test("max-limit", "fee_must_be_less_than_999999999", value => {
      return Number(toHalfWidth(value)) <= 999999999;
    }),
  TaxId: Yup.number().required("field_required").min(1, "field_required"),
  Description: Yup.string().max(300, "max_300_characters"),
  Remarks: Yup.string().max(300, "max_300_characters"),
  StartDate: Yup.date()
    .nullable()
    .typeError("invalid_date_format")
    .test(
      "valid-start-date",
      "invalid_date_format",
      function (value) {
        return value === null || dayjs(value).isValid();
      }
    )
    .test(
      "start-date-required-if-end-date-exists",
      "start_date_is_required_if_end_date_is_provided",
      function (value) {
        const endDate = this.parent.EndDate;
        return !endDate || (dayjs(value).isValid() && !!value);
      }
    ),
  EndDate: Yup.date()
    .nullable()
    .typeError("invalid_date_format")
    .test(
      "valid-end-date",
      "invalid_date_format",
      function (value) {
        return value === null || dayjs(value).isValid();
      }
    )
    .test(
      "end-date-required-if-start-date-exists",
      "end_date_is_required_if_start_date_is_provided",
      function (value) {
        const startDate = this.parent.StartDate;
        return !startDate || (dayjs(value).isValid() && !!value);
      }
    )
    .test(
      "end-date-must-be-after-start-date",
      "end_date_must_be_same_or_after_start_date",
      function (value) {
        const startDate = this.parent.StartDate;
        return !startDate || !value || (dayjs(value).isValid() && dayjs(startDate).isValid() && dayjs(value).isSameOrAfter(dayjs(startDate)));
      }
    ),

});



export const ReservationSettingSchema = Yup.object().shape({
  CancelationTime: Yup.mixed()
    .test("is-number", "cancellation_time_must_be_positive", value => {
      return !isNaN(Number(toHalfWidth(value)));
    })
    .test("is-positive", "cancellation_time_must_be_positive", value => {
      return Number(toHalfWidth(value)) >= 0;
    })
    .test("max-limit", "cancellation_time_cannot_exceed_48_hours", value => {
      return Number(toHalfWidth(value)) <= 48;
    }),

  NewReservationTime: Yup.mixed()
    .test("is-number", "new_reservation_time_must_be_positive", value => {
      return !isNaN(Number(toHalfWidth(value)));
    })
    .test("is-positive", "new_reservation_time_must_be_positive", value => {
      return Number(toHalfWidth(value)) >= 0;
    })
    .test("max-limit", "new_reservation_time_cannot_exceed_48_hours", value => {
      return Number(toHalfWidth(value)) <= 48;
    }),

  IntervalTime: Yup.number()
    .test("is-number", "interval_time_must_be_positive", value => {
      if (!value || value > 0) return true;
  }),

  CalendarIntervalTime: Yup.number()
    .test("is-number", "calender_interval_time_must_be_positive", value => {
      if (!value || value > 0) return true;
    }),

  SeatAvailable: Yup.mixed()
    .test("is-number", "seat_available_must_be_positive", value => {
      return !isNaN(Number(toHalfWidth(value)));
    })
    // .test("is-positive", "seat_available_must_be_positive", value => {
    //   if (!value) return true; // Allow null or empty values
    //   return Number(toHalfWidth(value)) > 0;
    // })
    ,

  BusinessSettings: Yup.array().of(
    Yup.object().shape({
      WeekDays: Yup.number().required("weekday_required"),
      IsClosed: Yup.boolean(),

      OpenTime: Yup.string()
        .nullable()
        .when("IsClosed", {
          is: false,
          then: (schema) =>
            schema
              // Conditional required
              .test("conditional-required", "field_required", function(value) {
                const indexMatch = this.path.match(/\[(\d+)\]/);
                const index = indexMatch ? parseInt(indexMatch[1], 10) : null;
                const isHoliday = this.options.context?.IsHoliday;

                // Skip required if index 7 and IsHoliday is false
                if (index === 7 && isHoliday === false) return true;

                // Otherwise, required
                return value != null && value !== "";
              })
              // Not midnight check
              .test("not-midnight", "field_required", function(value) {
                const indexMatch = this.path.match(/\[(\d+)\]/);
                const index = indexMatch ? parseInt(indexMatch[1], 10) : null;
                const isHoliday = this.options.context?.IsHoliday;

                if (index === 7 && isHoliday === false) return true;
                if (!value) return true;
                return value !== "00:00";
              })
              // Valid time format
              .test("valid-time", "invalid_time_format", function(value) {
                const indexMatch = this.path.match(/\[(\d+)\]/);
                const index = indexMatch ? parseInt(indexMatch[1], 10) : null;
                const isHoliday = this.options.context?.IsHoliday;

                if (index === 7 && isHoliday === false) return true;
                if (!value) return true;
                return moment(value, "HH:mm", true).isValid();
              }),
          otherwise: (schema) => schema.notRequired(),
        }),

        ClosedTime: Yup.string()
          .nullable()
          .when("IsClosed", {
            is: false,
            then: (schema) =>
              schema
                .test("conditional-required", "field_required", function(value) {
                  const indexMatch = this.path.match(/\[(\d+)\]/);
                  const index = indexMatch ? parseInt(indexMatch[1], 10) : null;
                  const isHoliday = this.options.context?.IsHoliday;

                  // Skip all checks if optional
                  if (index === 7 && isHoliday === false) return true;

                  // Required check
                  return value != null && value !== "";
                })
                .test("not-midnight", "field_required", function(value) {
                  const indexMatch = this.path.match(/\[(\d+)\]/);
                  const index = indexMatch ? parseInt(indexMatch[1], 10) : null;
                  const isHoliday = this.options.context?.IsHoliday;

                  // Skip check if field is optional
                  if (index === 7 && isHoliday === false) return true;

                  // Only fail if value is "00:00"
                  if (!value) return true; 
                  return value !== "00:00";
                })
                .test("valid-time", "invalid_time_format", function(value) {
                  const indexMatch = this.path.match(/\[(\d+)\]/);
                  const index = indexMatch ? parseInt(indexMatch[1], 10) : null;
                  const isHoliday = this.options.context?.IsHoliday;

                  if (index === 7 && isHoliday === false) return true;
                  if (!value) return true;
                  return moment(value, "HH:mm", true).isValid();
                })
                .test("OpenCloseTimeCheck", "opening_and_closing_time_cannot_be_same", function(value) {
                  const indexMatch = this.path.match(/\[(\d+)\]/);
                  const index = indexMatch ? parseInt(indexMatch[1], 10) : null;
                  const isHoliday = this.options.context?.IsHoliday;

                  if (index === 7 && isHoliday === false) return true;
                  if (!value) return true;
                  const { OpenTime } = this.parent;
                  return OpenTime && value ? OpenTime !== value : true;
                })
                .test("OpenCloseTimeCheck2", "closing_time_cannot_be_earlier_than_opening_time", function(value) {
                  const indexMatch = this.path.match(/\[(\d+)\]/);
                  const index = indexMatch ? parseInt(indexMatch[1], 10) : null;
                  const isHoliday = this.options.context?.IsHoliday;

                  if (index === 7 && isHoliday === false) return true;
                  if (!value) return true;
                  const { OpenTime } = this.parent;
                  return OpenTime && value ? moment(value, "HH:mm").isAfter(moment(OpenTime, "HH:mm")) : true;
                }),
            otherwise: (schema) => schema.notRequired(),
          }),


    })
  ),
  Precaution: Yup.string()
  .required('field_required')
});


export const validateCustomerRegisterNowSchema = Yup.object().shape({
  Email: Yup.string()
  .required('field_required')
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address"),

  Phone1: Yup.string()
    .matches(integerRegex, "only_numbers_are_allowed")
    .test("start-with-zero", "phone_number_must_start_with_0", (val) => {
      if (!val) return false;
      const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
      return digits.startsWith("0");
    })
    .test(
      "valid-japanese-phone",
      "telephone_10_or_11_length",
      (val) => {
        if (!val) return false;
        const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
        return digitsOnly.length === 10 || digitsOnly.length === 11;
      }
    )
    .required("field_required"),
  LastNameKana: Yup.string().required('field_required').matches(stringJpRegexValidate, "only_kana_are_allowed").max(30, "max_30_characters"),
  FirstNameKana: Yup.string().required('field_required').matches(stringJpRegexValidate, "only_kana_are_allowed").max(30, "max_30_characters"),
});

export const validateCustomerAuthCodeSchema = Yup.object().shape({
  Email: Yup.string()
  .required('field_required')
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address"),

  AuthCode: Yup.string()
    .matches(integerRegex, "only_numbers_are_allowed")
    .test("len", "only_4_digits_allowed", val => {
      const digitsOnly = val ? val : "";
      return digitsOnly.length == 4;
    })
    .required("field_required"),

});

const dobValidator = Yup.number().test(
  "dob-validation",
  "dob_cant_be_in_future",
  function () {
    const { DobYear, DobMonth, DobDate } = this.parent;

    if (DobYear === -1 && DobMonth === -1 && DobDate === -1) {
      return true;
    }

    if (DobYear === -1 || DobMonth === -1 || DobDate === -1) {
      return true;
    }

    const dob = new Date(DobYear, DobMonth - 1, DobDate);

    if (
      dob.getFullYear() !== DobYear ||
      dob.getMonth() !== DobMonth - 1 ||
      dob.getDate() !== DobDate
    ) {
      return this.createError({ message: "invalid_date" });
    }
    const today = new Date();
    if (dob > today) {
      return this.createError({ message: "dob_cant_be_in_future" });
    }

    return true;
  }
);

export const customerRegistrationSchema = Yup.object().shape({
  Email: Yup.string()
  .required('field_required')
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address"),

  Phone1: Yup.string()
    .matches(integerRegex, "only_numbers_are_allowed")
    .test("start-with-zero", "phone_number_must_start_with_0", (val) => {
      if (!val) return false;
      const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
      return digits.startsWith("0");
    })
    .test(
      "valid-japanese-phone",
      "telephone_10_or_11_length",
      (val) => {
        if (!val) return false;
        const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
        return digitsOnly.length === 10 || digitsOnly.length === 11;
      }
    )
    .required("field_required"),
  Password: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .required("field_required"),
  ConfirmPassword: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .oneOf([Yup.ref("Password")], "validation_new_confirm_password_mismatch")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .required("field_required"),
  DobYear: dobValidator,
  DobMonth: dobValidator,
  DobDate: dobValidator,
  PostalCode: Yup.string()
    .matches(integerNumJpRegex, "only_numbers_are_allowed")
    .length(7, "postal_code_max_7_characters"),

  LastName: Yup.string().max(30, "max_30_characters"),
  FirstName: Yup.string().max(30, "max_30_characters"),
  LastNameKana: Yup.string().required('field_required').matches(stringJpRegexValidate, "only_kana_are_allowed").max(30, "max_30_characters"),
  FirstNameKana: Yup.string().required('field_required').matches(stringJpRegexValidate, "only_kana_are_allowed").max(30, "max_30_characters"),
  Address: Yup.string()
    .max(256, "max_256_characters")
    .when("PostalCode", {
      is: (postal: string) => !postal || postal.trim() === "",
      then: (schema) =>
        schema.test(
          "postal-required-when-address",
          "postal_code_is_required_when_address_is_filled",
          function (value) {
            return !(value && value.trim().length > 0);
          }
        ),
      otherwise: (schema) => schema,
    })
});

export const EditCustomerProfileSchema = Yup.object().shape({
  Email: Yup.string()
    .required("email_is_required")
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address"),

  Phone1: Yup.string()
    .matches(integerRegex, "only_numbers_are_allowed")
    .test("start-with-zero", "phone_number_must_start_with_0", (val) => {
      if (!val) return false;
      const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
      return digits.startsWith("0");
    })
    .test(
      "valid-japanese-phone",
      "telephone_10_or_11_length",
      (val) => {
        if (!val) return false;
        const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
        return digitsOnly.length === 10 || digitsOnly.length === 11;
      }
    )
    .required("field_required"),
  DobYear: dobValidator,
  DobMonth: dobValidator,
  DobDate: dobValidator,
  PostalCode: Yup.string()
    .matches(integerNumJpRegex, "only_numbers_are_allowed")
    .length(7, "postal_code_max_7_characters"),
  Address: Yup.string()
    .max(256, "max_256_characters")
    .when("PostalCode", {
      is: (postal: string) => !postal || postal.trim() === "",
      then: (schema) =>
        schema.test(
          "postal-required-when-address",
          "postal_code_is_required_when_address_is_filled",
          function (value) {
            return !(value && value.trim().length > 0);
          }
        ),
      otherwise: (schema) => schema,
    })
    .test(
      "field_required_address",
      "field_required_address",
      function (value, context) {
        if (!value) return true;

        const original = value.replace(context?.parent?.PrefectureName || "", "");
        return original.trim() !== "";
      }
    ),
  LastName: Yup.string().max(30, "max_30_characters"),
  FirstName: Yup.string().max(30, "max_30_characters"),
  LastNameKana: Yup.string().required('field_required').matches(stringJpRegexValidate, "only_kana_are_allowed").max(30, "max_30_characters"),
  FirstNameKana: Yup.string().required('field_required').matches(stringJpRegexValidate, "only_kana_are_allowed").max(30, "max_30_characters"),

});

export const AddWalkInCustomerAccountSchema = Yup.object().shape({
  Email: Yup.string()
    .required("field_required")
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address"),
  LastNameKana: Yup.string().required('field_required').matches(stringJpRegexValidate, "only_kana_are_allowed").max(30, "max_30_characters"),
  FirstNameKana: Yup.string().required('field_required').matches(stringJpRegexValidate, "only_kana_are_allowed").max(30, "max_30_characters"),
  Phone1: Yup.string()
    .matches(integerRegex, "only_numbers_are_allowed")
    .test("start-with-zero", "phone_number_must_start_with_0", (val) => {
      if (!val) return false;
      const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
      return digits.startsWith("0");
    })
    .test(
      "valid-japanese-phone",
      "telephone_10_or_11_length",
      (val) => {
        if (!val) return false;
        const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
        return digitsOnly.length === 10 || digitsOnly.length === 11;
      }
    )
    .required("field_required"),
});

export const AddCustomerRegistrationSchema = Yup.object().shape({
  Email: Yup.string()
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address")
    .when("Password", {
      is: (val:string) => !!val && val.length > 0,
      then: (schema) => schema.required("field_required"),
      otherwise: (schema) => schema,
    }),
  Phone1: Yup.string()
    .matches(integerRegex, "only_numbers_are_allowed")
    .test("start-with-zero", "phone_number_must_start_with_0", (val) => {
      if (!val) return false;
      const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
      return digits.startsWith("0");
    })
    .test(
      "valid-japanese-phone",
      "telephone_10_or_11_length",
      (val) => {
        if (!val) return false;
        const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
        return digitsOnly.length === 10 || digitsOnly.length === 11;
      }
    )
    .required("field_required"),
  Password: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    ),
ConfirmPassword: Yup.string()
  .when("Password", {
    is: (val:any) => val && val.length > 0, // if password filled
    then: (schema) =>
      schema
        .required("field_required")
        .oneOf([Yup.ref("Password")], "validation_new_confirm_password_mismatch")
        .matches(
          /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
          "user_validation_password_matches"
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  DobYear: dobValidator,
  DobMonth: dobValidator,
  DobDate: dobValidator,
  PostalCode: Yup.string()
    .matches(integerNumJpRegex, "only_numbers_are_allowed")
    .length(7, "postal_code_max_7_characters"),

  LastName: Yup.string().max(30, "max_30_characters"),
  FirstName: Yup.string().max(30, "max_30_characters"),
  LastNameKana: Yup.string().required('field_required').matches(stringJpRegexValidate, "only_kana_are_allowed").max(30, "max_30_characters"),
  FirstNameKana: Yup.string().required('field_required').matches(stringJpRegexValidate, "only_kana_are_allowed").max(30, "max_30_characters"),
  Memo: Yup.string().max(300, "max_300_characters"),
  Address: Yup.string()
  .max(256, "max_256_characters")
  .when("PostalCode", {
    is: (postal: string) => !postal || postal.trim() === "",
    then: (schema) =>
      schema.test(
        "postal-required-when-address",
        "postal_code_is_required_when_address_is_filled",
        function (value) {
          return !(value && value.trim().length > 0);
        }
      ),
      otherwise: (schema) => schema,
    }),
  AppMemberShipCode: Yup.string()
    .max(256, "max_256_characters")
});


export const EditCustomerRegistrationSchema = Yup.object().shape({
  Email: Yup.string()
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address")
    .when("Password", {
      is: (val:string) => !!val && val.length > 0,
      then: (schema) => schema.required("field_required"),
      otherwise: (schema) => schema,
    }),
  ConfirmPassword: Yup.string()
    .when("Password", {
      is: (val:any) => val && val.length > 0, // if password filled
      then: (schema) =>
        schema
          .required("field_required")
          .oneOf([Yup.ref("Password")], "validation_new_confirm_password_mismatch")
          .matches(
            /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
            "user_validation_password_matches"
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
   Password: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    ),
  Phone1: Yup.string()
    .matches(integerRegex, "only_numbers_are_allowed")
    .test("start-with-zero", "phone_number_must_start_with_0", (val) => {
      if (!val) return false;
      const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
      return digits.startsWith("0");
    })
    .test(
      "valid-japanese-phone",
      "telephone_10_or_11_length",
      (val) => {
        if (!val) return false;
        const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
        return digitsOnly.length === 10 || digitsOnly.length === 11;
      }
    )
    .required("field_required"),
  DobYear: dobValidator,
  DobMonth: dobValidator,
  DobDate: dobValidator,
  PostalCode: Yup.string()
    .matches(integerNumJpRegex, "only_numbers_are_allowed")
    .length(7, "postal_code_max_7_characters"),

  LastName: Yup.string().max(30, "max_30_characters"),
  FirstName: Yup.string().max(30, "max_30_characters"),
  LastNameKana: Yup.string().required('field_required').matches(stringJpRegexValidate, "only_kana_are_allowed").max(30, "max_30_characters"),
  FirstNameKana: Yup.string().required('field_required').matches(stringJpRegexValidate, "only_kana_are_allowed").max(30, "max_30_characters"),
  Memo: Yup.string().max(300, "max_300_characters"),
  Address: Yup.string()
    .max(256, "max_256_characters")
    .when("PostalCode", {
      is: (postal: string) => !postal || postal.trim() === "",
      then: (schema) =>
        schema.test(
          "postal-required-when-address",
          "postal_code_is_required_when_address_is_filled",
          function (value) {
            return !(value && value.trim().length > 0);
          }
        ),
      otherwise: (schema) => schema,
    }),
  AppMemberShipCode: Yup.string()
    .max(256, "max_256_characters")
});

export const ReservationFormSchema = Yup.object().shape({
  selectedServices: Yup.array()
    .of(Yup.object())
    .min(1, "Please select at least one service."),

  selectedCustomer: Yup.string()
    .notOneOf(["-1"], "Please select a customer.")
    .required("Customer is required."),
});

export const BookingFormSchema = (
  formatMessage: (id: string) => string,
  slots: any,
  context?: any
) =>
  Yup.object().shape({
    selectedServices: Yup.array()
      .of(Yup.object())
      .min(1, () => formatMessage("please_select_at_least_one_service")),

    selectedStaff: Yup.string()
      .nullable()
      .required(() => formatMessage("please_select_a_staff_member")),
    selectedDate: Yup.string()
      .nullable()
      .test("is-valid-date", function (value) {
        const { mapSlot } = slots;
        //debugger; // you can inspect the value here
        let slotData  = mapSlot?.MapSlots;
        if (!value) {
          // You can dynamically control the message here
          return this.createError({ message: slotData?.length > 0 ?  formatMessage("please_select_a_date_and_time")  : formatMessage("no_date_time_available_slots") });
        }
        return true;
      }),
    agreedToTerms: Yup.boolean()
      .oneOf([true], () => formatMessage("you_must_agree_to_the_terms")),
    agreeToPrecaution: Yup.boolean()
      .oneOf([true], () => formatMessage("you_must_agree_to_the_precaution")),
  });


export const StoreAddStaffValidationSchema = Yup.object().shape({
  StaffName: Yup.string()
    .required("staff_name_is_required")
    .max(30, "max_30_characters"),
  TelephoneNumber: Yup.string()
    .required("field_required")
    // .matches(/^\+?[0-9\s-]+$/, "invalid_telephone_number"),
    .matches(integerRegex, "only_numbers_are_allowed")
    .test("start-with-zero", "phone_number_must_start_with_0", (val) => {
      if (!val) return false;
      const digits = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
      return digits.startsWith("0");
    })
    .test(
      "valid-japanese-phone",
      "telephone_10_or_11_length",
      (val) => {
        if (!val) return false;
        const digitsOnly = fullWidthToHalfWidth(val).replace(/[^\d]/g, "");
        return digitsOnly.length === 10 || digitsOnly.length === 11;
      }
    ),
  Email: Yup.string()
    .required("email_is_required")
    .email("invalid_email_address")
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "invalid_email_address"),
  PostCode: Yup.string()
    // .required("postal_code_is_required")
    .matches(integerNumJpRegex, "invalid_postal_code")
    .length(7, "postal_code_max_7_characters"),

  Address: Yup.string()
    .max(256, "max_256_characters")
    .test(
      "field_required_address",
      "field_required_address",
      function (value, context) {
        if (!value) return true;

        const original = value.replace(context?.parent?.PrefectureName || "", "");
        return original.trim() !== "";
      }
    ),

  Expertise: Yup.string().max(60, "max_60_characters"),
  AccountStatus: Yup.string()
    .required("status_is_required")
    .oneOf(["Valid", "Invalid"], "invalid_status_value"),
  NumberOfAvailability: Yup.string()
    .required("availability_is_required")
    .matches(/^[1-9]\d*$/, "invalid_availability")
    .max(50, "availability_max_50_characters"),
  NominationFee: Yup.number()
    .typeError("nomination_fee_must_be_valid_number")
    .max(10000, "nomination_fee_max_10000"),
  Password: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .required("field_required"),
  ConfirmPassword: Yup.string()
    .min(8, "min_length_required")
    .max(20, "min_length_required")
    .oneOf([Yup.ref("Password")], "validation_new_confirm_password_mismatch")
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      "user_validation_password_matches"
    )
    .required("field_required"),
  Introduction: Yup.string().max(300, "max_300_characters"),
  ShowOnReservationSite: Yup.boolean()
    .required("status_is_required")
    .oneOf([true, false], "invalid_status_value"),
});

export const paymentSettingsValidationSchema = Yup.object().shape({
  Name: Yup.string()
    // .matches(stringEnJpRegex, "payment_name_invalid_format")
    .required("field_required"),
  Description: Yup.string().max(300, "max_300_characters"),
});


export const accountingValidationSchema = Yup.object().shape({
  PaymentDetailsLists: Yup.array().of(
    Yup.object().shape({
      IsSelected: Yup.boolean(),
      Amount: Yup.string().when('IsSelected', (IsSelected, schema) => {
        return IsSelected
          ? schema
            .required('field_required')
            .matches(integerRegex, 'must_be_number')
          : schema.notRequired();
      }),
    })
  )
});

export const checkAgreementPaswordSchema = Yup.object().shape({
  Password: Yup.string()
    .required('field_required')
    .length(4, "4_digit_length_required")
});

export const addUpdateSaleRecordValidationSchema = Yup.object().shape({
  Date: Yup.date()
    .typeError("invalid_date_format")
    .required('field_required'),
  StaffId: Yup.number()
    .min(1, 'field_required'),
  Time: Yup.string()
    .required("end_time_is_required")
});

export const addSalesAgreementRecordValidationSchema = Yup.object().shape({
  AgreementId: Yup.number()
    .min(1, 'field_required')
});


export const discountValidateSchema = (itemDetails: any) => {
  let itemPrice = 0;
  let price = itemDetails?.saleItem?.Price || 0;
  let itemType = itemDetails?.saleItem?.ItemType || 0;
  let quantity = itemDetails?.saleItem?.Quantity || 1;
  itemPrice = price * quantity;

  return Yup.object().shape({
    DiscountType: Yup.number().required("field_required"),

    DiscountPercent: Yup.mixed()
      .nullable()
      .test("fullwidth-percent", "must_be_a_number", value => {
        if (value === null || value === undefined || value === "") return true;
        return !isNaN(Number(toHalfWidth(String(value))));
      })
      .when("DiscountType", {
        is: (val: number) => val === 0 || val === 2,
        then: schema =>
          schema.required("field_required").test(
            "percent-max",
            "percent_cannot_exceed_100",
            value => {
              const percentHW = Number(toHalfWidth(String(value)));
              return percentHW <= 100;
            }
          ),
        otherwise: schema => schema.notRequired()
      }),

    DiscountCash: Yup.mixed()
      .nullable()
      .test("fullwidth-cash", "must_be_a_number", value => {
        if (value === null || value === undefined || value === "") return true;
        return !isNaN(Number(toHalfWidth(String(value))));
      })
      .when("DiscountType", {
        is: (val: number) => val === 1 || val === 2,
        then: schema =>
        schema.required("field_required").test(
          "max-discount-cash",
          "discount_cannot_be_greater_than_item_price",
          function (value, context) {

            const cashHW = Number(toHalfWidth(String(value)));
            const percentHW = Number(toHalfWidth(String(context.parent.DiscountPercent)));

            // DiscountType = 1 → only cash discount
            if (context.parent.DiscountType === 1) {
              return cashHW <= itemPrice;
            }

            // DiscountType = 2 → both percent + cash
            if (context.parent.DiscountType === 2) {
              const remaining =
                itemPrice -
                (itemPrice * percentHW) / 100 -
                cashHW;

              return remaining >= 0;
            }

            return true;
          }
        ),
        otherwise: schema => schema.notRequired()
      })
  })
};

export const overAllDiscountValidateSchema = (subtotal: any) => {
  const itemPriceSubtotal= subtotal || 0;

  return Yup.object().shape({
    DiscountType: Yup.number().required("field_required"),

    DiscountPercent: Yup.number()
      .nullable()
      .when("DiscountType", {
        is: (val: number) => {
          return val === 0 || val === 2
        },
        then: schema => schema.required("field_required"),
        otherwise: schema => schema.notRequired(),
      })
      .when("DiscountType", {
        is: (val: number) => {
          return val === 0 || val === 2
        },
        then: schema => schema.max(100, "percent_cannot_exceed_100"),
        otherwise: schema => schema.notRequired(),
      })
    ,
    DiscountCash: Yup.number()
      .nullable()
      .when("DiscountType", {
        is: (val: number) => {
          return val === 1 || val === 2
        },
        then: schema => schema.required("field_required"),
        otherwise: schema => schema.notRequired(),
      })
      .when("DiscountType", {
        is: (val: number) => {
          return val === 1 || val === 2
        },
        then: schema => schema.test(
          "max-discount-cash",
          "discount_cannot_be_greater_than_item_price",
          function (value: any, context) {
            if (context.parent.DiscountType == 1) {
              return (value <= itemPriceSubtotal)
            }
            else if (context.parent.DiscountType == 2) {
              let itemPriceWithPercentDiscount = itemPriceSubtotal - (itemPriceSubtotal * context.parent.DiscountPercent) / 100 - value;
              return (itemPriceWithPercentDiscount >= 0)
            }
          }
        ),
        otherwise: schema => schema.notRequired(),
      })
  })
};

export const templateImageValidateSchema = Yup.object().shape({
  TemplateBeforeOne: Yup.string()
    .required('field_required'),
  TemplateBeforeTwo: Yup.string()
    .required('field_required'),
  TemplateAfterOne: Yup.string()
    .required('field_required'),
  TemplateAfterTwo: Yup.string()
    .required('field_required'),
});

export const cancelationPolicyValidateSchema = Yup.object().shape({
  HeadquarterCancelationPolicy: Yup.string()
    .required('field_required').max(500, "max_500_characters"),
  HeadquarterCancelationText: Yup.string()
    .required('field_required').max(300, "max_300_characters"),
});

export const AddInventorySchema = Yup.object().shape({
  AdjustedQuantity: Yup.mixed()
    .test("is-required", "field_required", function (value) {
      return !(value === undefined && this.parent.AddType === addInventoryTypeEnum.AdjustedQuantity);
    })
    .test("is-number", "must_be_a_number", function (value) {
      if (this.parent.AddType !== addInventoryTypeEnum.AdjustedQuantity) return true;
      return !isNaN(Number(toHalfWidth(value)));
    })
    .test("not-zero", "must_greater_be_zero", function (value) {
      if (this.parent.AddType !== addInventoryTypeEnum.AdjustedQuantity) return true;
      return Number(toHalfWidth(value)) > 0;
    })
    .test("max-limit", "must_be_less_than_999999999", function (value) {
      if (this.parent.AddType !== addInventoryTypeEnum.AdjustedQuantity) return true;
      return Math.abs(Number(toHalfWidth(value))) <= 999999999;
    }),

  Notes: Yup.string()
    .test("max-length-300", "max_300_characters", function (value) {
    if (this.parent.AddType === addInventoryTypeEnum.AdjustedQuantity && value) {
      return value.length <= 300;
    }
    return true;
  }),
  InternalUsedQuantity: Yup.mixed()
    .test("is-required", "field_required", function (value) {
      return !(value === undefined && this.parent.AddType === addInventoryTypeEnum.InternalUse);
    })
    .test("is-number", "must_be_a_number", function (value) {
      if (this.parent.AddType !== addInventoryTypeEnum.InternalUse) return true;
      return !isNaN(Number(toHalfWidth(value)));
    })
    .test("is-positive", "must_be_positive_number", function (value) {
      if (this.parent.AddType !== addInventoryTypeEnum.InternalUse) return true;
      return Number(toHalfWidth(value)) > 0;
    })
    .test("max-limit", "must_be_less_than_999999999", function (value) {
      if (this.parent.AddType !== addInventoryTypeEnum.InternalUse) return true;
      return Number(toHalfWidth(value)) <= 999999999;
    }),

  IncomingQuantity: Yup.mixed()
    .test("is-required", "field_required", function (value) {
      return !(value === undefined && this.parent.AddType === addInventoryTypeEnum.Incoming);
    })
    .test("is-number", "must_be_a_number", function (value) {
      if (this.parent.AddType !== addInventoryTypeEnum.Incoming) return true;
      return !isNaN(Number(toHalfWidth(value)));
    })
    .test("is-positive", "must_be_positive_number", function (value) {
      if (this.parent.AddType !== addInventoryTypeEnum.Incoming) return true;
      return Number(toHalfWidth(value)) > 0;
    })
    .test("max-limit", "must_be_less_than_999999999", function (value) {
      if (this.parent.AddType !== addInventoryTypeEnum.Incoming) return true;
      return Number(toHalfWidth(value)) <= 999999999;
    }),
});
export const dailyTotallingFilterSchema = Yup.object().shape({
  Date: Yup.date()
    .required("field_required")
    .typeError("invalid_date_format")
    .test(
      "valid-start-date",
      "invalid_date_format",
      function (value) {
        return value === null || dayjs(value).isValid();
      }
    )
    .max(
      dayjs().endOf("day").toDate(),
      "date_cannot_be_in_the_future"
    ),
});
export const weeklyTotallingFilterSchema = Yup.object().shape({
  StartDate: Yup.date()
    .required("field_required")
    .typeError("invalid_date_format")
    .test(
      "valid-start-date",
      "invalid_date_format",
      function (value) {
        return value === null || dayjs(value).isValid();
      }
    )
    .max(
      dayjs().endOf("day").toDate(),
      "date_cannot_be_in_the_future"
    ),
});

export const periodTotallingFilterSchema = Yup.object().shape({
  StartDate: Yup.date()
    .required("field_required")
    .typeError("invalid_date_format")
    .test("valid-start-date", "invalid_date_format", function (value) {
      return value === null || dayjs(value).isValid();
    })
    .max(dayjs().endOf("day").toDate(), "date_cannot_be_in_the_future"),

  EndDate: Yup.date()
    .required("field_required")
    .typeError("invalid_date_format")
    .test("valid-end-date", "invalid_date_format", function (value) {
      return value === null || dayjs(value).isValid();
    })
    .max(dayjs().endOf("day").toDate(), "date_cannot_be_in_the_future")
    .test(
      "end-date-must-be-after-start-date",
      "end_date_must_be_same_or_after_start_date",
      function (value) {
        const { StartDate } = this.parent;
        if (!value || !StartDate) return true; // skip if one is missing
        return dayjs(value).isSameOrAfter(dayjs(StartDate), "day");
      }
    ),
});

export const staffTotallingFilterSchema = Yup.object().shape({
  StartDate: Yup.date()
    .required("field_required")
    .typeError("invalid_date_format")
    .test("valid-start-date", "invalid_date_format", function (value) {
      return value === null || dayjs(value).isValid();
    })
    .max(dayjs().endOf("day").toDate(), "date_cannot_be_in_the_future"),

  EndDate: Yup.date()
    .required("field_required")
    .typeError("invalid_date_format")
    .test("valid-end-date", "invalid_date_format", function (value) {
      return value === null || dayjs(value).isValid();
    })
    // .max(dayjs().endOf("day").toDate(), "date_cannot_be_in_the_future")
    .test(
      "end-date-must-be-after-start-date",
      "end_date_must_be_same_or_after_start_date",
      function (value) {
        const { StartDate } = this.parent;
        if (!value || !StartDate) return true; // skip if one is missing
        return dayjs(value).isSameOrAfter(dayjs(StartDate), "day");
      }
    ),
});

export const monthLyTotallingFilterSchema = Yup.object().shape({
  MonthYear:Yup.date()
    .nullable()
    .typeError("invalid_date_month_format")
    .max(
      dayjs().endOf("month").toDate(),
      "month_year_cannot_be_in_the_future"
    ),
});

export const advanceSearchValidationSchema: Yup.ObjectSchema<any> 
  = Yup.object({
  IsVisitCanBe: Yup.boolean(),
  IsVisitCantBe: Yup.boolean(),

  VisitStartDate: Yup.string()
    .nullable()
    .when(['IsVisitCanBe', 'IsVisitCantBe'], {
      is: (canBe: boolean, cantBe: boolean) => canBe || cantBe,
      then: (schema) => schema.test(
        "ValidVisitStartDateFormat",
        "invalid_date_format",
        (value) =>
          !value ||
          (value !== "Invalid Date" && !isNaN(new Date(value).getTime()))
      ),
      otherwise: (schema) => schema,
    }),

  VisitEndDate: Yup.string()
    .nullable()
    .when(['IsVisitCanBe', 'IsVisitCantBe'], {
      is: (canBe: boolean, cantBe: boolean) => canBe || cantBe,
      then: (schema) => schema
        .test(
          "ValidVisitEndDateFormat",
          "invalid_date_format",
          (value) =>
            !value ||
            (value !== "Invalid Date" && !isNaN(new Date(value).getTime()))
        )
        .test(
          "VisitEndDateAfterStartDate",
          "end_date_must_be_same_or_later_than_start_date",
          function (value) {
            const { VisitStartDate } = this.parent || {};
            if (!value || !VisitStartDate) return true;
            if (value === "Invalid Date" || VisitStartDate === "Invalid Date") return true;
            return new Date(value) >= new Date(VisitStartDate);
          }
        ),
      otherwise: (schema) => schema,
    }),

  IsLastVisitCanBe: Yup.boolean(),
  IsLastVisitCantBe: Yup.boolean(),

  LastVisitStartDate: Yup.string()
    .nullable()
    .when(['IsLastVisitCanBe', 'IsLastVisitCantBe'], {
      is: (canBe: boolean, cantBe: boolean) => canBe || cantBe,
      then: (schema) => schema.test(
        "ValidLastVisitStartDateFormat",
        "invalid_date_format",
        (value) =>
          !value ||
          (value !== "Invalid Date" && !isNaN(new Date(value).getTime()))
      ),
      otherwise: (schema) => schema,
    }),

  LastVisitEndDate: Yup.string()
    .nullable()
    .when(['IsLastVisitCanBe', 'IsLastVisitCantBe'], {
      is: (canBe: boolean, cantBe: boolean) => canBe || cantBe,
      then: (schema) => schema
        .test(
          "ValidLastVisitEndDateFormat",
          "invalid_date_format",
          (value) =>
            !value ||
            (value !== "Invalid Date" && !isNaN(new Date(value).getTime()))
        )
        .test(
          "LastVisitEndDateAfterStartDate",
          "end_date_must_be_same_or_later_than_start_date",
          function (value) {
            const { LastVisitStartDate } = this.parent || {};
            if (!value || !LastVisitStartDate) return true;
            if (value === "Invalid Date" || LastVisitStartDate === "Invalid Date") return true;
            return new Date(value) >= new Date(LastVisitStartDate);
          }
        ),
      otherwise: (schema) => schema,
    }),

  Dob: Yup.string()
    .nullable()
    .test(
      "DobRequiredIfEndDOB",
      "field_required",
      (value, context) => {
        const endDob = context?.parent?.EndDOB;
        if (endDob && !value) return false;
        return true;
      }
    )
    .test(
      "ValidDOBFormat",
      "invalid_date_format",
      (value) => !value || (value !== "Invalid Date" && !isNaN(new Date(value).getTime()))
    ),

  EndDOB: Yup.string()
    .nullable()
    .when("Dob", {
      is: (dob: any) => !!dob,
      then: (schema) =>
        schema
          .test("EndDOBRequiredIfDob", "field_required", (value, context) => {
            const dob = context?.parent?.Dob;
            if (dob && !value) return false;
            return true;
          })
          .test(
            "ValidEndDOBFormat",
            "invalid_date_format",
            (value) => !!value && value !== "Invalid Date" && !isNaN(new Date(value).getTime())
          )
          .test(
            "EndDOBAfterDOB",
            "end_date_must_be_greater",
            function (value) {
              const { Dob } = this.parent || {};
              if (!value || !Dob) return true;
              if (value === "Invalid Date" || Dob === "Invalid Date") return true;
              return new Date(value) > new Date(Dob);
            }
          ),
      otherwise: (schema) =>
        schema.test(
          "ValidEndDOBFormatOptional",
          "invalid_date_format",
          (value) => !value || (value !== "Invalid Date" && !isNaN(new Date(value).getTime()))
        ),
    }),

  NumberOfVisitTo: Yup.string()
    .nullable()
    .test(
      "ValidNumberOfVisitTo",
      "must_be_number",
      (value) => !value || !isNaN(Number(toHalfWidth(value)))
    )
    .test(
      "VisitToGreaterThanFrom",
      "end_value_must_be_greater",
      function (value) {
        const { NumberOfVisit } = this.parent || {};
        if (!value || !NumberOfVisit) return true;
        return Number(toHalfWidth(value)) >= Number(toHalfWidth(NumberOfVisit));
      }
    ),
  })


export const invertoryFilterValidateSchema = Yup.object().shape({
  MonthYear:Yup.date()
    .nullable()
    .typeError("invalid_date_month_format")
    .max(
      dayjs().endOf("month").toDate(),
      "month_year_cannot_be_in_the_future"
    ),
  ProductName: Yup.string()
    .max(120, "max_120_characters"),
});

export const addCmsDetailsValidationSchema = Yup.object().shape({
StoreDetails: Yup.string()
  .transform((value) => {
    if (!value) return value;

    // Remove HTML tags
    const stripped = value.replace(/<[^>]*>/g, "");
    return stripped;
  })
  .notRequired()
  .test(
    "max-150",
    "max_150_characters",
    (value) => !value || Array.from(value).length <= 150
  
  ),
  StoreMapURL: Yup.string()
  .notRequired()  
  .matches(iframeRegex, "valid_iframe_code"),
  AccessInformation:Yup.string()
  .notRequired()
    .max(60, "max_60_characters"),
  PhoneMessageVisible: Yup.boolean()
    .oneOf([true, false], "invalid_status_value"),
  StorePhoneMessage:Yup.string()
    .when("PhoneMessageVisible", {
        is: (val: boolean) => val === true,
        then: schema => schema.max(60, "max_60_characters"),
        otherwise: schema => schema.notRequired(),
      })
});

export const EmailSettingValidateSchema = (
  userType: string,
) => {
  return Yup.object().shape({
    EmailBeforeHours: Yup.number().when("EmailType", {
      is: (val: any) => val === emailTypeEnum.ReservationReminder,
      then: (schema) => schema.required("field_required"),
      otherwise: (schema) => schema
    }),

    EmailAfterHours: Yup.number().when("EmailType", {
      is: (val: any) => val === emailTypeEnum.VisitingThanks,
      then: (schema) => schema.required("field_required"),
      otherwise: (schema) => schema
    }),

    EmailBody: Yup.string().when("EmailType", {
      is: (val: any) => (val !== emailTypeEnum.HappyBirthday 
        && userType !== userTypeEnum[userTypeEnum.Store]),
      then: (schema) => schema
      .test('required', 'field_required', (value) => {
      const plainTextLength = getPlainTextLength(value || '');
      return plainTextLength >= 1;
    })
    .test('maxLength', 'max_1000_characters', (value) => {
      const plainTextLength = getPlainTextLength(value || '');
      return plainTextLength <= 1000;
    }),
      otherwise: (schema) => schema
    }),

    EmailHeader: Yup.string().when("EmailType", {
      is: (val: any) => (val === emailTypeEnum.HappyBirthday && userType !== userTypeEnum[userTypeEnum.Store]),
      then: (schema) => schema
      .test('required', 'field_required', (value) => {
      const plainTextLength = getPlainTextLength(value || '');
      return plainTextLength >= 1;
    })
    .test('maxLength', 'max_1000_characters', (value) => {
      const plainTextLength = getPlainTextLength(value || '');
      return plainTextLength <= 1000;
    }),
      otherwise: (schema) => schema
    }),

    EmailFooter: Yup.string().when("EmailType", {
      is: (val: any) => (val === emailTypeEnum.HappyBirthday && userType !== userTypeEnum[userTypeEnum.Store]),
      then: (schema) => schema
      .test('required', 'field_required', (value) => {
      const plainTextLength = getPlainTextLength(value || '');
      return plainTextLength >= 1;
    })
    .test('maxLength', 'max_1000_characters', (value) => {
      const plainTextLength = getPlainTextLength(value || '');
      return plainTextLength <= 1000;
    }),
      otherwise: (schema) => schema
    }),

    BeforeDays: Yup.number().when("EmailType", {
      is: (val: any) => val === emailTypeEnum.HappyBirthday,
      then: (schema) => schema.required("field_required"),
      otherwise: (schema) => schema
    }),

    BeforeHours: Yup.number().when("EmailType", {
      is: (val: any) => val === emailTypeEnum.HappyBirthday,
      then: (schema) =>
        schema.required("field_required").max(23, "must_be_less_than_24"),
      otherwise: (schema) => schema
    }),

    StoreEmailBody: Yup.string().when("EmailType", {
      is: (val: any) =>
        val === emailTypeEnum.HappyBirthday &&
        userType === userTypeEnum[userTypeEnum.Store],
      then: (schema) =>
        schema.test("maxLength", "max_1000_characters", (value) => {
          const plainTextLength = getPlainTextLength(value || "");
          return plainTextLength <= 1000;
        }),
      otherwise: (schema) => schema,
    }),
  });
};

export const addCmsWorkingDaysValidationSchema = Yup.object().shape({
  IsVisible: Yup.boolean(),
  IsHolidayVisible: Yup.boolean(),

  BusinessSettings: Yup.array().when("IsVisible", {
    is: false,
    then: (schema) =>
      schema.of(
        Yup.object().shape({
          WeekDays: Yup.number().required("weekday_required"),
          IsClosed: Yup.boolean(),

          OpenTime: Yup.string()
            .nullable()
            .when("IsClosed", {
              is: false,
              then: (schema) =>
                schema.test("not-midnight", "field_required", (value) => value !== "00:00"),
              otherwise: (schema) => schema.notRequired(),
            }),

          ClosedTime: Yup.string()
            .nullable()
            .when("IsClosed", {
              is: false,
              then: (schema) =>
                schema
                  .test("not-midnight", "field_required", (value) => value !== "00:00")
                  .test("OpenCloseTimeCheck", "opening_and_closing_time_cannot_be_same", function (values) {
                    let { OpenTime } = this.parent;
                    if (OpenTime && values) {
                      return OpenTime !== values;
                    }
                    return true;
                  })
                  .test("OpenCloseTimeCheck2", "closing_time_cannot_be_earlier_than_opening_time", function (values) {
                    let { OpenTime } = this.parent;
                    if (OpenTime && values) {
                      return values > OpenTime;
                    }
                    return true;
                  }),
              otherwise: (schema) => schema.notRequired(),
            }),
        })
      ),
    otherwise: (schema) => schema.notRequired(), // No validation if IsVisible is false
  }),

  HolidaysMessage: Yup.string()
  .max(20, "max_20_characters")
  // .when("IsHolidayVisible", {
  //   is: true,
  //   then: (schema) => schema.required("field_required").max(20, "max_20_characters"),
  //   otherwise: (schema) => schema.notRequired(),
  // }),
});
